import {
  Component,
  ElementRef,
  effect,
  NO_ERRORS_SCHEMA,
  OnDestroy,
  OnInit,
  ViewChild,
  Signal,
  computed,
  DestroyRef,
} from '@angular/core';
import { Directionality } from '@angular/cdk/bidi';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EventV2Document } from '@models/alerts-v2.model';
import { select, Store } from '@ngrx/store';
import { AlertEventsActions } from '@states/alert-events/alert-events.action-types';
import { AlertEventsSelectors } from '@states/alert-events/alert-events.selector-types';
import { LocationSelectors } from '@states/location/location.selector-types';
import Fuse, { IFuseOptions } from 'fuse.js';
import {
  filter,
  Observable,
  pairwise,
  startWith,
  Subscription,
  take,
} from 'rxjs';
import { LocationModel } from 'src/app/locations/location.model';
import { AppState } from 'src/app/store/app.state';
import { Router } from '@angular/router';
import { routerSegments } from '@consts/routes';
import { WallV2Selectors } from '@states/wall-v2/wall-v2.selector-types';
import { WallV2Model } from '@models/wall-v2.model';
import { EntityLookupModel } from '@models/entity-lookup.model';
import { SearchableSelectors } from '@states/searchable/searchable.selector-types';
import { UiCameraStatusComponent } from '../../ui-kit/ui-camera-status/ui-camera-status.component';
import { UiCameraSnapshotComponent } from '../../ui-kit/ui-camera-snapshot/ui-camera-snapshot.component';
import { FormsModule } from '@angular/forms';
import {
  CommonModule,
  DatePipe,
  KeyValuePipe,
  NgFor,
  NgIf,
  NgTemplateOutlet,
} from '@angular/common';
import { CameraSelectors } from '@states/camera/camera.selector-types';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import {
  ActiveOrganization,
  ThumbsBackendTunnelType,
} from '@models/organization.model';
import { PipesModule } from '../../../pipes/pipes.module';
import { LocationHealthModalComponent } from '../location-health-modal/location-health-modal.component';
import {
  CameraStatsModalData,
  SingleCameraStatsModalComponent,
} from '../single-camera-stats-modal/single-camera-stats-modal.component';
import { EventSentenceRendererComponent } from '../../components/event-sentence-renderer/event-sentence-renderer.component';
import { UiKitModule } from '../../ui-kit/ui-kit.module';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { AlarmModel } from '@models/alarm.model';
import { AlarmScheduleControlsComponent } from '../../components/alarm-schedule-controls/alarm-schedule-controls.component';
import { SocketMainService } from '../../../socket/socket-main.service';
import { SocketEvents } from '../../../socket/socket.model';
import { CamerasActions } from '@states/cameras/cameras.action-types';
import { CamerasCreateType } from '@enums/cameras.enum';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { TruncateDirective } from '../../directives/truncate.directive';
import { UiCopyClipboardComponent, UiToggleComponent } from '@ui';
import { PeopleService } from '../../../development/people.service';
import {
  extractOrgAssetPath,
  GO_TUNNEL_BASE_MAP,
  StorageBucket,
} from '@consts/storage-url.util';
import { organizationAssetsAwsUrl } from '@consts/url.const';
import { AuthBgDirective } from '../../directives/auth-bg.directive';
import { AuthSrcDirective } from '../../directives/auth-src.directive';
import { ConfirmDialogModel } from '@ui';
import { SharedActions } from '@states/shared/shared.action-types';
import { EditExistingPersonDialogComponent } from '../../../pages/database/pages/people/dialogs/edit-existing-person-dialog/edit-existing-person-dialog.component';
import { UiUserSettingsDateComponent } from '../../ui-kit/ui-user-settings-date/ui-user-settings-date.component';
import { SearchGlobalModel } from '@models/search-global.model';
import { InlineChatPanelComponent } from './components/inline-chat-panel/inline-chat-panel.component';
import { IntelligenceResultsService } from '../../../services/intelligence-results.service';
import { IntelligenceModels } from '@models/intelligence.model';
import {
  VIRTUAL_SCROLL_ENABLE_THRESHOLDS,
  VIRTUAL_SCROLL_THRESHOLDS,
} from './search-global-modal.const';
import { SearchGlobalModalHelper } from './search-global-modal.utils';
import { DisableButtonPermissionGuardDirective } from './disable-button-permission-guard.directive';
import { PermissionModel } from '@models/permission.model';
import { DeviceLocationModel } from '../../../pages/location-v2/pages/device/models';
import { GlobalSearchSettingsModalComponent } from '@share-user-account-settings';
import { UserSettingsModels } from '@share-models';
import { UserSettingsActions } from '@states/user-settings/user-settings.action-types';
import { UserSettingsSelectors } from '@states/user-settings/user-settings.selector-types';
import { SearchableActions } from '@states/searchable/searchable.action-types';
import { MatProgressBar } from '@angular/material/progress-bar';
import { UserSelectors } from '@states/user/user.selector-types';
import { cameraEditUserMenuLevel2, cameraEditDeveloperMenuLevel2 } from '@consts/menu.const';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const ADD_CAMERA_PANEL_CLASS = 'search-global-modal--add-camera';
const EXPANDED_PANEL_CLASS = 'search-global-modal--expanded';
const SEARCH_GLOBAL_MODAL_PANEL_CLASS = 'search-global-modal';
const AI_DIALOG_HEIGHT = 'clamp(520px, 70vh, 660px)';

@Component({
  selector: 'app-search-global-modal',
  templateUrl: './search-global-modal.component.html',
  styleUrls: ['./search-global-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    UiCameraSnapshotComponent,
    UiCameraStatusComponent,
    NgIf,
    NgFor,
    KeyValuePipe,
    NgTemplateOutlet,
    DatePipe,
    PipesModule,
    EventSentenceRendererComponent,
    UiKitModule,
    TranslatePipe,
    MatTooltip,
    MatIcon,
    MatCheckbox,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    AlarmScheduleControlsComponent,
    ScrollingModule,
    TruncateDirective,
    UiCopyClipboardComponent,
    UiToggleComponent,
    UiUserSettingsDateComponent,
    DisableButtonPermissionGuardDirective,
    MatProgressBar,
    AuthBgDirective,
    AuthSrcDirective,
    InlineChatPanelComponent,
  ],
  schemas: [NO_ERRORS_SCHEMA],
})
export class SearchGlobalModalComponent implements OnInit, OnDestroy {
  // Make Math available in template
  protected readonly Math = Math;

  private flattenWorkerSub?: Subscription;

  entityLookup: EntityLookupModel.EntityLookupDocument[] = [];

  activeIndex = -1;
  searchTerm = '';
  allItems: any[] = [];
  searchResults: SearchGlobalModel.Types.SearchItem[] = [];
  groupedResults: Partial<
    Record<
      SearchGlobalModel.Common.CategoryId,
      SearchGlobalModel.Types.SearchItem[]
    >
  > = {};
  flatResults: SearchGlobalModel.Types.SearchItem[] = [];
  previewResult: SearchGlobalModel.Types.SearchItem | null = null;

  // Camera list search term
  cameraSearchTerm = '';

  // Edge list search term
  edgeSearchTerm = '';

  // Speaker list search term
  speakerSearchTerm = '';

  // Building navigation search term
  buildingNavigationSearchTerm = '';

  // Floor navigation search term
  floorNavigationSearchTerm = '';

  // Sub-location list search term
  subLocationSearchTerm = '';

  // Location list search term
  locationSearchTerm = '';

  // Event list search term
  eventSearchTerm = '';

  // People list search term
  peopleSearchTerm = '';

  // Location preview active view
  activeLocationView: SearchGlobalModel.Views.LocationView = 'edges';

  // People preview active view
  activePeopleView: SearchGlobalModel.Views.PeopleView = 'edges';

  // Building preview active view
  activeBuildingView: SearchGlobalModel.Views.BuildingView = 'floors';

  // Floor preview active view
  activeFloorView: SearchGlobalModel.Views.FloorView = 'edges';

  // Sub-location preview active view
  activeSubLocationView: SearchGlobalModel.Views.LocationView = 'edges';

  // Wall preview active view
  activeWallView: SearchGlobalModel.Views.WallView = 'edges';

  // Event preview active view
  activeEventView: SearchGlobalModel.Views.EventView = 'edges';

  // Speaker preview active view
  activeSpeakerView: SearchGlobalModel.Views.SpeakerView = 'edges';

  protected readonly virtualScrollThresholds = VIRTUAL_SCROLL_THRESHOLDS;
  protected readonly virtualScrollEnableThresholds =
    VIRTUAL_SCROLL_ENABLE_THRESHOLDS;

  private hoverTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastKeyboardActivity = 0;
  private isMouseOverPreview = false;
  private isKeyboardSelected = false; // Track if current selection is from keyboard
  private isMouseInFloorPreview = false; // Specific flag for floor preview area
  private floorPreviewTimeout: ReturnType<typeof setTimeout> | null = null; // Timeout for floor preview flag
  isCommandMode = false; // Track if we're in command mode
  private commandOperator = SearchGlobalModel.Common.COMMAND_OPERATOR; // Operator to trigger command mode
  isCategoryFilterMode = false; // Track if we're in category filter mode
  private categoryFilterOperator =
    SearchGlobalModel.Common.CATEGORY_FILTER_OPERATOR; // Operator to trigger category filter mode
  private aiOperator = SearchGlobalModel.Common.AI_OPERATOR; // Operator to trigger AI chat mode
  isAiMode = false; // Track if we're in AI chat mode
  isAiDialogExpanded = false;
  /** Text typed after the `?` entry operator, handed to the composer as its initial value. */
  aiInitialPrompt = '';
  /**
   * Prompt to send the moment the chat shell mounts. Only the `?` + Enter path
   * sets this: typing the operator alone stays in plain search, so the palette
   * does not jump into chat halfway through a question.
   */
  aiAutoSubmitPrompt = '';
  /**
   * Viewport `top` of the search input at the moment chat mode was entered, so
   * the composer can FLIP from there to its docked position.
   */
  composerFlipFromTop: number | null = null;
  /** Plain-search state captured on entry, restored when chat mode is left. */
  private searchTermBeforeAiMode = '';
  private selectedCategoriesBeforeAiMode: SearchGlobalModel.Common.CategoryId[] =
    [];
  private isCategoryFilterModeBeforeAiMode = false;
  /** Synchronous flag kept in sync with isAiUiAgent$ for use in guards. */
  isAiUiAgentEnabled = false;
  selectedCategories: SearchGlobalModel.Common.CategoryId[] = []; // Currently selected categories for filtering
  availableCategories: SearchGlobalModel.Common.CategoryId[] =
    SearchGlobalModel.Common.AVAILABLE_CATEGORIES;
  filteredCategories: SearchGlobalModel.Common.CategoryId[] = []; // For autocomplete
  showCategoryAutocomplete = false; // Show/hide category autocomplete dropdown
  activeCategoryIndex = -1; // Track active category in autocomplete
  suggestedCategoryFull: string | null = null; // Best match category for ghost suggestion
  private reopenAutocompleteOnFocus = false; // Reopen category list on focus if fragment exists

  public selectIsDeveloperSig: Signal<boolean> = this.store$.selectSignal(UserSelectors.isDeveloper);
  public cameraEditTabsSig = computed(() =>
    this.selectIsDeveloperSig()
      ? cameraEditDeveloperMenuLevel2
      : cameraEditUserMenuLevel2
  );

  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('textMeasurer') textMeasurerRef?: ElementRef<HTMLSpanElement>;
  @ViewChild(MatMenuTrigger) menuTrigger?: MatMenuTrigger;

  ghostPrefixWidth = 0;
  ghostOffset = 0;
  ghostReady = false;

  public alarmStatusChangeString = AlarmModel.ArmModeString;

  protected readonly permissions = PermissionModel.Permissions;
  private readonly defaultGlobalSearchSettings: UserSettingsModels.GlobalSearchSettingsModel =
    {
      markResult: true,
      includeAllMatch: true,
    };

  private fuse!: Fuse<SearchGlobalModel.Types.SearchItem>;
  public selectUserSettings$: Observable<UserSettingsModels.UserSettingsDocument | null> =
    this.store$.pipe(select(UserSettingsSelectors.selectSelectedUserSettings));

  public globalSearchSettings: UserSettingsModels.GlobalSearchSettingsModel =
    this.defaultGlobalSearchSettings;
  /** True only for users with the `ag-ui-agent` role. Controls AI toggle button visibility and `?` operator access. */
  public isAiUiAgent$: Observable<boolean> = this.store$.pipe(
    select(UserSelectors.isAiUiAgent),
  );

  public selectLastSyncedAt$: Observable<number | null> = this.store$.pipe(
    select(SearchableSelectors.selectLastSyncedAt()),
  );
  public selectSyncProgress$: Observable<number> = this.store$.pipe(
    select(SearchableSelectors.selectSyncProgressPercentage()),
  );
  public selectShowProgressing$: Observable<boolean> = this.store$.pipe(
    select(SearchableSelectors.selectShowProgressing()),
  );

  constructor(
    private store$: Store<AppState>,
    private router: Router,
    private dialogRef: MatDialogRef<SearchGlobalModalComponent>,
    private dialog: MatDialog,
    private socketMainService: SocketMainService,
    private peopleService: PeopleService,
    private translateService: TranslateService,
    private translate: TranslateService,
    public directionality: Directionality,
    private destroyRef: DestroyRef,
    private intelligenceResultsService: IntelligenceResultsService,
  ) {
    this.dialogRef.addPanelClass(SEARCH_GLOBAL_MODAL_PANEL_CLASS);

    effect(() => {
      const isAddCameraActive =
        this.intelligenceResultsService.current()?.type ===
        IntelligenceModels.CameraProvisioning.PanelResultType.AddCamera;
      this.updateDialogSize(isAddCameraActive);
    });
  }

  /** Expand or restore the AI chat dialog without invoking browser fullscreen. */
  toggleAiDialogExpanded(): void {
    this.isAiDialogExpanded = !this.isAiDialogExpanded;
    const isAddCameraActive =
      this.intelligenceResultsService.current()?.type ===
      IntelligenceModels.CameraProvisioning.PanelResultType.AddCamera;
    this.updateDialogSize(isAddCameraActive);
  }

  /** Keeps all dialog-size updates in one place, including result changes. */
  private updateDialogSize(isAddCameraActive: boolean): void {
    this.dialogRef.removePanelClass(ADD_CAMERA_PANEL_CLASS);
    this.dialogRef.removePanelClass(EXPANDED_PANEL_CLASS);

    if (this.isAiDialogExpanded) {
      this.dialogRef.addPanelClass(EXPANDED_PANEL_CLASS);
      this.dialogRef.updateSize('100vw', '100vh');
      return;
    }

    if (isAddCameraActive) {
      this.dialogRef.addPanelClass(ADD_CAMERA_PANEL_CLASS);
    }
    this.dialogRef.updateSize(
      isAddCameraActive ? '95vw' : '80vw',
      this.isAiMode ? AI_DIALOG_HEIGHT : '',
    );
  }

  getTypeI18nKey(type: string): string {
    return SearchGlobalModalHelper.getTypeI18nKey(type);
  }

  ngOnDestroy(): void {
    // A pick-camera tool handler may still be awaiting a selection when the
    // palette closes; release it so that turn can finish.
    this.intelligenceResultsService.cancelCameraPick();
    this.intelligenceResultsService.cancelAddCamera();
    this.flattenWorkerSub?.unsubscribe();

    // Clean up any pending timeouts
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
    if (this.floorPreviewTimeout) {
      clearTimeout(this.floorPreviewTimeout);
    }
  }

  public locations$: Observable<LocationModel.LocationItem[]> =
    this.store$.pipe(select(LocationSelectors.selectAllLocations));
  public events$: Observable<EventV2Document[]> = this.store$.pipe(
    select(AlertEventsSelectors.selectEvents),
  );
  public walls$: Observable<WallV2Model.WallMongoDocument[]> = this.store$.pipe(
    select(WallV2Selectors.selectAllWalls),
  );

  public searchable$: Observable<SearchGlobalModel.SearchableItem[]> =
    this.store$.pipe(
      select(SearchableSelectors.selectAllSearchablesFilteredByPermissions),
    );

  // Event preview related observables
  public selectAllCameras$ = this.store$.select(
    CameraSelectors.selectAllCameras,
  );
  public selectAllLocations$ = this.store$.select(
    LocationSelectors.selectAllLocations,
  );
  public selectActiveOrganization$: Observable<ActiveOrganization> =
    this.store$.select(OrganizationSelectors.selectActiveOrganization);
  public selectLocationLookup$: Observable<{
    [key: string]: LocationModel.LocationItem;
  }> = this.store$.pipe(select(LocationSelectors.selectLocationEntities));

  ngOnInit(): void {
    this.isAiUiAgent$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((allowed) => (this.isAiUiAgentEnabled = !!allowed));

    // Two-stage Escape. The dialog is opened with disableClose, so closing is
    // ours to do: first Escape leaves AI chat mode, otherwise it closes the
    // palette. Uses the dialog's own keydown stream rather than a document
    // listener, and only acts when this dialog is the topmost one — otherwise a
    // nested camera-health or confirm dialog's Escape would pop chat mode.
    this.dialogRef
      .keydownEvents()
      .pipe(
        filter((event) => event.key === 'Escape'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        if (event.defaultPrevented) return;
        const openDialogs = this.dialog.openDialogs;
        if (openDialogs[openDialogs.length - 1] !== this.dialogRef) return;

        event.preventDefault();
        event.stopPropagation();

        if (this.isAiMode) {
          this.exitAiMode();
          return;
        }
        this.dialogRef.close();
      });

    this.selectShowProgressing$
      .pipe(
        startWith(false),
        pairwise(),
        filter(([wasSyncing, isSyncing]) => wasSyncing && !isSyncing),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        setTimeout(() => {
          this.onSearch();
        }, 300);
      });

    this.selectUserSettings$.subscribe((res) => {
      this.globalSearchSettings =
        res?.globalSearchSettings ?? this.defaultGlobalSearchSettings;
    });
    this.searchable$
      .pipe(filter((items) => !!items?.length))
      .subscribe((items) => {
        this.allItems = items;
        this.initializeFuse();
      });

    // Subscribe to alarm state changes via socket
    this.socketMainService
      .consume<any>(SocketEvents.alarmSyncEdgeStatusChangedNotification)
      .pipe(
        filter((res) => !!res.document),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res: any) => {
        if (
          this.previewResult?.type === 'alarm' &&
          this.previewResult.alarmId === res.document._id
        ) {
          this.previewResult.state = res.document.state;
          this.previewResult.calculatedState = res.document.calculatedState;
          this.previewResult.stateUpdatedAt = res.document.stateUpdatedAt;
        }
      });
  }

  initializeFuse(): void {
    const keys = [
      'name',
      'subInfo',
      'raw.name',
      'raw.city',
      'raw.address',
      'raw.description',
      'raw.retentionDays',
      'raw.ipAddress',
      'raw.cameras.cameraName',
      'raw.edges.edgeName',
      'raw.locations.locationName',
      'raw.subLocations.subLocationName',
      'raw.speakers.speakerName',
      'raw.floors.floorName',
      'raw.buildings.buildingName',
    ];

    const options: IFuseOptions<any> = {
      keys,
      threshold: 0.3,
      includeScore: true,
      includeMatches: true,
    };

    this.fuse = new Fuse(this.allItems, options);
  }

  // Docs preview is disabled for now.
  // openDocsFullscreen(article: SearchGlobalModel.Items.ArticleItem | Extract<SearchGlobalModel.Types.NavResult, {
  //   type: 'article'
  // }>): void {
  //   this.dialog.open(DocsPreviewFullScreenComponent, {
  //     panelClass: 'docs-preview-fullscreen',
  //     data: article,
  //     autoFocus: false,
  //   });
  // }

  // viewDocsOriginal(article: SearchGlobalModel.Items.ArticleItem): void {
  //   if (!article?.htmlUrl) return;
  //   window.open(article.htmlUrl, '_blank', 'noopener');
  // }

  // getArticlePreviewHtml(article: SearchGlobalModel.Items.ArticleItem): string {
  //   const html = article?.raw?.bodyHtml || '';
  //   return SearchGlobalModalHelper.stripInlineColor(html);
  // }

  toggleAiMode(): void {
    if (this.isAiMode) {
      this.exitAiMode();
      return;
    }
    // A question already drafted after '?' is carried into the composer instead
    // of being dropped — same as the Tab shortcut, prefill only, no auto-send.
    this.enterAiMode(this.isAiPromptDraft() ? this.getAiPromptDraft() : '');
  }

  /**
   * Switch the palette into the AI chat shell. The `?` operator is only an entry
   * shortcut: it is stripped here and `searchTerm` never holds it afterwards —
   * from this point the composer owns all input. `autoSubmit` sends the seed
   * immediately, which is what `?` + Enter does.
   */
  enterAiMode(seedPrompt = '', autoSubmit = false): void {
    if (!this.isAiUiAgentEnabled || this.isAiMode) return;

    // An operator-prefixed term is a question, not a search query — restoring it
    // on exit would re-trigger the entry path and trap the palette in chat mode.
    this.searchTermBeforeAiMode = this.isAiPromptDraft() ? '' : this.searchTerm;
    this.selectedCategoriesBeforeAiMode = [...this.selectedCategories];
    this.isCategoryFilterModeBeforeAiMode = this.isCategoryFilterMode;

    // Measured before the input is hidden so the composer can FLIP from it.
    this.composerFlipFromTop =
      this.searchInputRef?.nativeElement?.getBoundingClientRect().top ?? null;

    this.isAiMode = true;
    this.updateDialogSize(false);
    this.aiInitialPrompt = autoSubmit ? '' : seedPrompt;
    this.aiAutoSubmitPrompt = autoSubmit ? seedPrompt : '';
    this.searchTerm = '';
    this.isCommandMode = false;
    this.resetCategoryFilterState();
    this.clearResults();
  }

  /**
   * True while the input holds a `?`-prefixed draft question. The palette stays
   * in plain search in this state and only switches on Enter.
   */
  isAiPromptDraft(): boolean {
    return (
      this.isAiUiAgentEnabled &&
      !this.isAiMode &&
      this.searchTerm.startsWith(this.aiOperator)
    );
  }

  /** The question typed after the `?` operator, without the operator itself. */
  getAiPromptDraft(): string {
    return this.searchTerm.slice(this.aiOperator.length).trim();
  }

  /** Leave chat mode, restore the previous plain-search state, refocus the input. */
  exitAiMode(): void {
    if (!this.isAiMode) return;

    // A pick-camera handler may still be awaiting a selection.
    this.intelligenceResultsService.cancelCameraPick();
    this.intelligenceResultsService.cancelAddCamera();

    this.isAiDialogExpanded = false;
    this.isAiMode = false;
    this.updateDialogSize(false);
    this.aiInitialPrompt = '';
    this.aiAutoSubmitPrompt = '';
    this.composerFlipFromTop = null;
    this.searchTerm = this.searchTermBeforeAiMode;
    this.selectedCategories = [...this.selectedCategoriesBeforeAiMode];
    this.isCategoryFilterMode = this.isCategoryFilterModeBeforeAiMode;

    setTimeout(() => {
      this.focusMainSearchInput();
      this.onSearch();
    }, 0);
  }

  focusMainSearchInput(): void {
    this.searchInputRef?.nativeElement?.focus();
  }

  onSearch(): void {
    const term = this.searchTerm.trim();

    // A '?'-prefixed term is a question being drafted, not a search query: stay
    // in plain search, drop stale results, and wait for Enter to open the chat.
    if (this.isAiPromptDraft()) {
      this.isCommandMode = false;
      this.resetCategoryFilterState();
      this.clearResults();
      return;
    }

    // First, exit command mode if we no longer have the operator
    if (this.isCommandMode && !term.startsWith(this.commandOperator)) {
      this.isCommandMode = false;
    }

    // If input is empty, reset views and stop
    if (!term) {
      this.resetCategoryFilterState();
      this.isCommandMode = false;
      this.clearResults();
      return;
    }

    // Command mode
    if (term.startsWith(this.commandOperator)) {
      this.isCommandMode = true;
      this.resetCategoryFilterState();
      this.searchResults = [];
      this.groupedResults = {};
      this.flatResults = [];
      this.activeIndex = -1;
      this.previewResult = null;
      return;
    }

    // Category mode (visibility-only filtering, multi-category via repeated --)
    let searchTerm = term;
    if (term.startsWith(this.categoryFilterOperator)) {
      this.isCategoryFilterMode = true;
      this.isCommandMode = false;

      const tokens = term.split(/\s+/).filter((t) => t.length > 0);

      // No tokens beyond '--' => show all categories
      if (tokens.length === 1 && tokens[0] === this.categoryFilterOperator) {
        this.selectedCategories = [];
        this.filteredCategories = this.sortCategoriesForFragment(
          '',
          [],
        ) as SearchGlobalModel.Common.CategoryId[];
        this.showCategoryAutocomplete = true;
        this.activeCategoryIndex = 0;
        this.suggestedCategoryFull = this.filteredCategories[0] || null;
        this.reopenAutocompleteOnFocus = true;
        this.clearResults();
        return;
      }

      // Collect categories from tokens that start with '--'
      const categories: SearchGlobalModel.Common.CategoryId[] =
        [] as unknown as SearchGlobalModel.Common.CategoryId[];
      for (const tok of tokens) {
        if (tok.startsWith(this.categoryFilterOperator) && tok.length > 2) {
          const rawCat = tok.substring(2);
          const mapped = this.getMatchingCategoryId(rawCat);
          if (
            mapped &&
            this.availableCategories.includes(mapped) &&
            !categories.includes(mapped)
          ) {
            categories.push(mapped);
          }
        }
      }

      // Check if we're ready for a new category
      const endsWithSpace = this.searchTerm.endsWith(' ');

      // Find the last category token (could be anywhere in the list)
      let lastCategoryToken = '';
      let hasIncompleteCategory = false;

      for (let i = tokens.length - 1; i >= 0; i--) {
        if (tokens[i].startsWith(this.categoryFilterOperator)) {
          lastCategoryToken = tokens[i];
          break;
        }
      }

      if (lastCategoryToken) {
        const rawFrag = lastCategoryToken.substring(2);
        const mappedId = this.getMatchingCategoryId(rawFrag);
        hasIncompleteCategory = !mappedId;
      }

      // Only show autocomplete when there's an incomplete category being typed
      // Don't auto-show when user finishes a complete category with space
      if (hasIncompleteCategory) {
        // Show autocomplete for the incomplete category
        const rawFrag = lastCategoryToken.substring(2);
        const mappedId = this.getMatchingCategoryId(
          rawFrag,
        ) as SearchGlobalModel.Common.CategoryId | null;
        const frag = (mappedId || rawFrag).toLowerCase();
        let list = this.sortCategoriesForFragment(
          frag,
          categories as unknown as string[],
        ) as SearchGlobalModel.Common.CategoryId[];
        if (list.length === 0) {
          // Fallback to all available (excluding already selected) so user always has options
          list = this.sortCategoriesForFragment(
            '',
            categories as unknown as string[],
          ) as SearchGlobalModel.Common.CategoryId[];
        }
        this.filteredCategories = list;
        this.showCategoryAutocomplete = true;
        this.activeCategoryIndex = this.filteredCategories.length ? 0 : -1;
        this.suggestedCategoryFull = this.filteredCategories.length
          ? SearchGlobalModalHelper.getCategoryLocalizedLabel(
              this.translate,
              this.filteredCategories[0],
            )
          : null;
        this.reopenAutocompleteOnFocus = true;
      } else {
        // No incomplete categories - hide autocomplete and wait for user input
        // User can either type a search term or start a new category with "--"
        this.filteredCategories = [];
        this.showCategoryAutocomplete = false;
        this.activeCategoryIndex = -1;
        this.suggestedCategoryFull = null;
        this.reopenAutocompleteOnFocus = false;
      }

      // Build search term from tokens that are not category tokens
      const searchTokens = tokens.filter(
        (p) => !p.startsWith(this.categoryFilterOperator),
      );
      searchTerm = this.stripOuterQuotes(searchTokens.join(' ').trim());
      // Normalize selected categories: localized label → id
      this.selectedCategories = categories.map(
        (c) =>
          (this.getMatchingCategoryId(
            c,
          ) as SearchGlobalModel.Common.CategoryId) ||
          (c as SearchGlobalModel.Common.CategoryId),
      );

      if (!searchTerm) {
        this.clearResults();
        return;
      }
    } else {
      // Not in category mode anymore
      if (this.isCategoryFilterMode) {
        this.resetCategoryFilterState();
      }
    }

    if (this.isCommandMode) {
      return;
    }

    // Trim outer quotes for general searches as well
    searchTerm = this.stripOuterQuotes(searchTerm);

    // Perform search (same logic for both modes)
    const fuseResults = this.fuse.search(searchTerm);

    const results: SearchGlobalModel.Types.SearchItem[] = fuseResults.map(
      ({ item, score, matches }) => {
        const typed: SearchGlobalModel.Types.SearchItem = {
          ...(item as SearchGlobalModel.Types.SearchItem),
          name: item.name || '',
          subInfo: item.subInfo || '',
          score: score,
          matches: matches as any,
        };
        return typed;
      },
    );

    const grouped = new Map<
      SearchGlobalModel.Common.CategoryId,
      SearchGlobalModel.Types.SearchItem[]
    >();
    for (const item of results) {
      const group = grouped.get(item.type) || [];
      group.push(item);
      grouped.set(item.type, group);
    }

    this.groupedResults = Object.fromEntries([...grouped.entries()]);

    // Flat list for keyboard nav
    if (this.isCategoryFilterMode && this.selectedCategories.length > 0) {
      const ordered: SearchGlobalModel.Types.SearchItem[] = [];
      for (const cat of this.selectedCategories) {
        const arr = grouped.get(cat) || [];
        if (arr.length) ordered.push(...arr);
      }
      this.flatResults = ordered;
    } else {
      // Use the same order as availableCategories for consistent keyboard navigation
      // Optimized: single loop instead of filter + flatMap
      const ordered: SearchGlobalModel.Types.SearchItem[] = [];
      for (const cat of this.availableCategories) {
        const arr = grouped.get(cat);
        if (arr && arr.length) ordered.push(...arr);
      }
      this.flatResults = ordered;
    }

    this.searchResults = results;
    this.activeIndex = -1;

    // Handle preview when results change
    if (this.flatResults.length === 0) {
      // No results - clear preview
      this.previewResult = null;
    } else if (this.previewResult) {
      // We have results and a current preview
      // Check if the current preview result still exists in new results
      const currentPreviewStillExists = this.flatResults.some(
        (result) =>
          result._id === this.previewResult._id &&
          result.type === this.previewResult.type,
      );

      if (!currentPreviewStillExists) {
        // Current preview is no longer valid, clear it
        // The user will see a preview when they hover over a new result or use keyboard navigation
        this.previewResult = null;
      }
      // If current preview still exists, keep it - this prevents unnecessary clearing
    }
    // If no preview exists and we have results, don't auto-set one
    // Let user interaction (hover or keyboard) determine what to show

    // No-op for ghost metrics (transparent prefix overlay approach)
    setTimeout(() => this.updateGhostMetrics(), 0);
  }

  private stripOuterQuotes(value: string): string {
    if (!value) return value;
    const first = value.charAt(0);
    const last = value.charAt(value.length - 1);
    if ((first === '"' && last === '"') || (first === "'" && last === "'")) {
      return value.substring(1, value.length - 1).trim();
    }
    return value;
  }

  hasActiveSearchTerm(): boolean {
    if (this.isCommandMode) return false;
    if (!this.isCategoryFilterMode) {
      return this.searchTerm.trim().length > 0;
    }
    const catTerm = this.getCategorySearchTerm?.() || '';
    return this.selectedCategories.length > 0 && catTerm.trim().length > 0;
  }

  private interactingWithAutocomplete = false;

  onAutocompleteMouseDown(event: MouseEvent): void {
    // Mark that we are clicking inside the autocomplete so blur won't close it
    this.interactingWithAutocomplete = true;
  }

  onInputBlur(): void {
    // Close autocomplete when input loses focus, unless we're interacting with it
    setTimeout(() => {
      if (this.interactingWithAutocomplete) {
        // Reset the flag and keep the autocomplete open
        this.interactingWithAutocomplete = false;
        return;
      }
      this.showCategoryAutocomplete = false;
      // If user is in category mode and has an incomplete fragment, remember to reopen on focus
      if (this.isCategoryFilterMode) {
        const frag = this.getCategoryInput();
        // Only reopen autocomplete if we have an incomplete category fragment
        // Don't reopen for complete categories
        const isIncompleteCategory =
          !!frag &&
          !this.availableCategories.includes(
            frag.toLowerCase() as SearchGlobalModel.Common.CategoryId,
          );
        this.reopenAutocompleteOnFocus =
          isIncompleteCategory ||
          this.searchTerm.trim() === this.categoryFilterOperator;
      } else {
        this.reopenAutocompleteOnFocus = false;
      }
    }, 0);
  }

  onInputFocus(): void {
    // Simple focus handling - just restore autocomplete visibility if needed
    if (this.isCategoryFilterMode && this.reopenAutocompleteOnFocus) {
      this.showCategoryAutocomplete = true;
    }

    // Reset the reopen flag
    this.reopenAutocompleteOnFocus = false;

    // Update ghost metrics
    setTimeout(() => this.updateGhostMetrics(), 0);
  }

  isAutocompleteVisible(): boolean {
    // Simple logic: show autocomplete if we're in category mode and it's enabled
    return this.isCategoryFilterMode && this.showCategoryAutocomplete;
  }

  private clearResults(): void {
    this.searchResults = [];
    this.groupedResults = {};
    this.flatResults = [];
    this.activeIndex = -1;
    this.previewResult = null;
  }

  private resetCategoryFilterState(): void {
    this.isCategoryFilterMode = false;
    this.selectedCategories = [];
    this.showCategoryAutocomplete = false;
    this.activeCategoryIndex = -1;
    this.filteredCategories = [];
    this.suggestedCategoryFull = null;
  }

  onKeyDown(event: KeyboardEvent): void {
    const total = this.flatResults.length;

    // Typing '?' into an empty input opens the draft with its trailing space
    // already there, so the question starts where the hint says it does. Only
    // the keystroke that creates the draft is handled: a space the user then
    // deletes must stay deleted, which a value-level fix could not honour.
    if (
      event.key === this.aiOperator &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey &&
      this.isAiUiAgentEnabled &&
      !this.isAiMode &&
      !this.isCommandMode &&
      !this.isCategoryFilterMode
    ) {
      const input = this.searchInputRef?.nativeElement;
      const replacesWholeTerm =
        !!input &&
        input.selectionStart === 0 &&
        input.selectionEnd === input.value.length;

      if (replacesWholeTerm) {
        event.preventDefault();
        this.searchTerm = `${this.aiOperator} `;
        this.onSearch();
        return;
      }
    }

    // Enter on a '?'-prefixed draft is what opens the chat shell — the question
    // must be complete before the palette changes shape. An empty draft is
    // ignored so a lone '?' does not switch modes.
    if (event.key === 'Enter' && this.isAiPromptDraft()) {
      const prompt = this.getAiPromptDraft();
      if (!prompt) return;
      event.preventDefault();
      this.enterAiMode(prompt, true);
      return;
    }

    // Tab is the keyboard entry shortcut into the AI chat shell. Category mode
    // uses Tab for its own completion, so it is excluded below. Unlike Enter it
    // only prefills the composer — Tab is not a send gesture.
    if (
      event.key === 'Tab' &&
      this.isAiUiAgentEnabled &&
      !this.isAiMode &&
      !this.isCommandMode &&
      !this.isCategoryFilterMode
    ) {
      event.preventDefault();
      this.enterAiMode(
        this.isAiPromptDraft()
          ? this.getAiPromptDraft()
          : this.searchTerm.trim(),
      );
      return;
    }

    // Handle command mode
    if (this.isCommandMode) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.executeCommand();
        return;
      }
      return; // Don't handle other keys in command mode
    }

    // Handle category filter mode
    if (this.isCategoryFilterMode) {
      const hasCategory = !!this.selectedCategories.length;
      const termInCategory = this.getCategorySearchTerm?.() || '';
      const hasSearchTerm = termInCategory.trim().length > 0;
      const isFilteringPhase =
        this.showCategoryAutocomplete || !hasCategory || !hasSearchTerm;

      if (isFilteringPhase) {
        if (event.key === 'Enter') {
          event.preventDefault();
          // If autocomplete is shown, select the highlighted (or first) category
          if (
            this.showCategoryAutocomplete &&
            this.filteredCategories.length > 0
          ) {
            const idx =
              this.activeCategoryIndex >= 0 ? this.activeCategoryIndex : 0;
            this.selectCategory(
              this.filteredCategories[
                idx
              ] as SearchGlobalModel.Common.CategoryId,
            );
            return;
          }
          // If no list but we have a suggested best match, select it
          if (!this.showCategoryAutocomplete && this.suggestedCategoryFull) {
            const mapped = this.getMatchingCategoryId(
              this.suggestedCategoryFull,
            );
            if (mapped) {
              this.selectCategory(mapped);
            }
            return;
          }
          // Otherwise, perform the search
          this.onSearch();
          return;
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          this.resetCategoryFilterState();
          this.searchTerm = '';
          this.clearResults();
          return;
        }

        // Handle arrow keys for category autocomplete
        if (
          this.showCategoryAutocomplete &&
          this.filteredCategories.length > 0
        ) {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.navigateCategoryAutocomplete(1);
            this.scrollActiveCategoryIntoView();
            return;
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.navigateCategoryAutocomplete(-1);
            this.scrollActiveCategoryIntoView();
            return;
          }
          if (event.key === 'Tab') {
            event.preventDefault();

            // Check if we have an incomplete category fragment
            const currentFragment = this.getCategoryInput();
            const isIncompleteFragment =
              !!currentFragment &&
              !this.availableCategories.includes(
                currentFragment.toLowerCase() as SearchGlobalModel.Common.CategoryId,
              );

            if (isIncompleteFragment && this.filteredCategories.length > 0) {
              // Only auto-complete if we have an incomplete fragment
              const idx =
                this.activeCategoryIndex >= 0 ? this.activeCategoryIndex : 0;
              this.selectCategory(
                this.filteredCategories[
                  idx
                ] as SearchGlobalModel.Common.CategoryId,
              );
            } else if (this.suggestedCategoryFull && isIncompleteFragment) {
              // Only use suggested category if we have an incomplete fragment
              const mapped = this.getMatchingCategoryId(
                this.suggestedCategoryFull,
              );
              if (mapped) {
                this.selectCategory(mapped);
              }
            }
            // If category is complete, Tab does nothing (user should add space manually)
            return;
          }
        }
        return; // Don't handle other keys during filtering phase
      }
      // If not in filtering phase, fall through to normal navigation below
    }

    if (!total) return;

    if (['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) {
      event.preventDefault(); // Prevent default scrolling behavior
    }

    // Handle ArrowDown navigation
    if (event.key === 'ArrowDown') {
      this.lastKeyboardActivity = Date.now(); // Only set when actually navigating
      this.isKeyboardSelected = true; // Mark as keyboard selection
      this.activeIndex++;
      if (this.activeIndex >= total) {
        this.activeIndex = 0; // Cycle back to the start
      }

      this.updatePreview();
      this.scrollActiveItemIntoView();
    }

    // Handle ArrowUp navigation
    if (event.key === 'ArrowUp') {
      this.lastKeyboardActivity = Date.now(); // Only set when actually navigating
      this.isKeyboardSelected = true; // Mark as keyboard selection
      this.activeIndex--;
      if (this.activeIndex < 0) {
        this.activeIndex = total - 1; // Cycle back to the end
      }

      this.updatePreview();
      this.scrollActiveItemIntoView();
    }

    // Handle Enter key to navigate to the active result
    if (event.key === 'Enter' && this.activeIndex >= 0) {
      this.navigateToResult(this.flatResults[this.activeIndex]);
    }
  }

  scrollActiveItemIntoView(): void {
    setTimeout(() => {
      const activeEl = document.querySelector('.result-item.active');
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }, 0);
  }

  navigateToResult(result: SearchGlobalModel.Types.NavResult): void {
    if (result?.type === 'camera') {
      const route = [
        routerSegments.cameras,
        routerSegments.cameraView,
        result.locationId,
        result.edgeId,
        result.cameraId,
      ];
      this.router.navigate(route);
    }

    if (result?.type === 'speaker') {
      this.router.navigate(
        [
          routerSegments.locationV3,
          routerSegments.devices,
          routerSegments.list,
        ],
        {
          queryParams: {
            types: DeviceLocationModel.DeviceType.SPEAKER,
            term: result.name,
          },
        },
      );
    }

    if (result?.type === 'location') {
      this.router.navigate(
        [
          routerSegments.locationV3,
          routerSegments.devices,
          routerSegments.list,
        ],
        {
          queryParams: {
            term: result.name,
          },
        },
      );
    }

    if (result?.type === 'sublocation') {
      this.router.navigate(
        [
          routerSegments.locationV3,
          routerSegments.devices,
          routerSegments.list,
        ],
        {
          queryParams: {
            term: result.name,
          },
        },
      );
    }

    if (result?.type === 'page') {
      // Parse the URL to handle both with and without query parameters
      const urlParts = result.url.split('?');
      const path = urlParts[0];
      const queryString = urlParts[1] || '';

      // Convert path to route segments (remove leading slash and split)
      const pathSegments = path.split('/').filter((segment) => segment);

      // Parse query parameters
      const queryParams: any = {};
      if (queryString) {
        const params = new URLSearchParams(queryString);
        params.forEach((value, key) => {
          queryParams[key] = value;
        });
      }

      this.router.navigate(pathSegments, {
        queryParams: queryParams,
      });
    }

    if (result?.type === 'wall') {
      const route = [routerSegments.wallV2, routerSegments.view, result._id];
      this.router.navigate(route);
    }

    if (result?.type === 'event') {
      const route = [routerSegments.alertsV2, routerSegments.edit, result._id];
      this.router.navigate(route);
    }

    if (result?.type === 'alarm') {
      const route = [
        routerSegments.alertsV2,
        routerSegments.alarms,
        routerSegments.list,
      ];
      this.router.navigate(route, {
        queryParams: { search: result.name },
      });
    }

    if (result?.type === 'core') {
      this.router.navigate(
        [
          routerSegments.locationV3,
          routerSegments.devices,
          routerSegments.list,
        ],
        {
          queryParams: {
            term: result.name,
            types: DeviceLocationModel.DeviceType.CORE,
          },
        },
      );
    }

    if (result?.type === 'person') {
      const route = [routerSegments.database, routerSegments.people];
      this.router.navigate(route, {
        queryParams: { search: result.name },
      });
    }

    if (result?.type === 'building') {
      this.navigateToBuilding({
        buildingId: result.buildingId,
        name: result.name,
      });
    }

    if (result?.type === 'floor') {
      this.navigateToFloor(result.buildingId, result.floorId);
    }

    // if (result?.type === 'article') {
    //   this.openDocsFullscreen(result);
    //   return;
    // }

    this.dialogRef.close();
  }

  navigateToPage(page: SearchGlobalModel.Items.PageItem): void {
    // Parse the URL to handle both with and without query parameters
    const urlParts = page.url.split('?');
    const path = urlParts[0];
    const queryString = urlParts[1] || '';

    // Convert path to route segments (remove leading slash and split)
    const pathSegments = path.split('/').filter((segment) => segment);

    // Parse query parameters
    const queryParams: any = {};
    if (queryString) {
      const params = new URLSearchParams(queryString);
      params.forEach((value, key) => {
        queryParams[key] = value;
      });
    }

    this.router.navigate(pathSegments, {
      queryParams: queryParams,
    });

    this.dialogRef.close();
  }

  close(): void {
    this.dialogRef.close();
  }

  isImageUrl(icon: string): boolean {
    return (
      icon.startsWith('http://') ||
      icon.startsWith('https://') ||
      icon.startsWith('data:') ||
      icon.startsWith('/')
    );
  }

  onResultHover(result: any): void {
    // Don't trigger hover if keyboard was used recently (within 500ms instead of 2 seconds)
    const timeSinceKeyboard = Date.now() - this.lastKeyboardActivity;
    if (timeSinceKeyboard < 500) {
      return;
    }

    // Clear any existing timeouts
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }

    // Use timeout to prevent flickering when moving mouse quickly between items
    this.hoverTimeout = setTimeout(() => {
      this.setPreview(result, false); // false = from mouse hover
      // Clear keyboard selection flag when mouse is actively used
      this.isKeyboardSelected = false;
    }, 50); // Short delay to prevent flickering
  }

  onResultLeave(): void {
    // Don't clear preview if keyboard was used recently or mouse is over preview
    const timeSinceKeyboard = Date.now() - this.lastKeyboardActivity;
    if (timeSinceKeyboard < 500 || this.isMouseOverPreview) {
      return;
    }

    // Clear keyboard selection flag when mouse is actively used
    this.isKeyboardSelected = false;

    // Clear any existing timeouts
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }

    // Don't clear the preview immediately - let it persist
    // The preview will be updated when hovering over another item or when keyboard navigation takes over
    // This prevents the flickering effect and maintains a consistent user experience
  }

  onPreviewMouseEnter(): void {
    this.isMouseOverPreview = true;
  }

  onPreviewMouseLeave(): void {
    this.isMouseOverPreview = false;
    // Don't clear preview when leaving preview panel
    // The preview should persist until:
    // 1. User hovers over a different search result
    // 2. User uses keyboard navigation
    // 3. Search results change
    // This provides a more stable and predictable user experience
  }

  // Handle when mouse leaves the entire search results area
  onSearchResultsMouseLeave(): void {
    // Don't switch back to keyboard if mouse is in floor preview (camera cone interaction)
    if (this.isMouseInFloorPreview) {
      return;
    }

    // Add a small delay to prevent issues when moving between elements
    setTimeout(() => {
      // When mouse leaves the search results area entirely, only restore keyboard priority
      // if there's a valid keyboard selection AND keyboard was actually used
      if (
        this.activeIndex >= 0 &&
        this.activeIndex < this.flatResults.length &&
        this.lastKeyboardActivity > 0
      ) {
        this.isKeyboardSelected = true;

        // Update preview to show the keyboard-selected item
        this.updatePreview();
      }
    }, 100); // Small delay to prevent flickering
  }

  // Surgical fix: Floor preview specific mouse handlers with delayed flag reset
  onFloorPreviewMouseEnter(): void {
    this.isMouseInFloorPreview = true;
    // Clear any pending timeout
    if (this.floorPreviewTimeout) {
      clearTimeout(this.floorPreviewTimeout);
      this.floorPreviewTimeout = null;
    }
  }

  onFloorPreviewMouseLeave(): void {
    // Don't immediately set to false - use a delay to handle camera cone interactions
    this.floorPreviewTimeout = setTimeout(() => {
      this.isMouseInFloorPreview = false;
    }, 50); // Very short delay to handle movement between elements
  }

  // Camera cone specific handlers to prevent preview switching
  onCameraConeMouseEnter(): void {
    this.isMouseInFloorPreview = true;
    // Clear any pending timeout - camera cone interaction keeps flag true
    if (this.floorPreviewTimeout) {
      clearTimeout(this.floorPreviewTimeout);
      this.floorPreviewTimeout = null;
    }
  }

  onCameraConeMouseLeave(): void {
    // Use the same delayed approach as floor preview
    this.floorPreviewTimeout = setTimeout(() => {
      this.isMouseInFloorPreview = false;
    }, 50); // Very short delay
  }

  // Method to clear mouse state when keyboard navigation takes over
  private clearMouseState(): void {
    // Only clear the preview if we're actually switching to keyboard navigation
    // and there's a valid active index to show
    if (this.activeIndex >= 0 && this.activeIndex < this.flatResults.length) {
      // Let updatePreview() handle setting the new preview
      // Don't clear here - this prevents unnecessary flickering
    } else {
      // Only clear if there's no valid selection
      this.previewResult = null;
    }
  }

  /**
   * Centralized function to set or clear preview based on result type
   * @param result The result to preview, or null to clear preview
   * @param isFromKeyboard Whether this preview update is from keyboard navigation
   */
  private setPreview(
    result: SearchGlobalModel.Types.SearchItem | null,
    isFromKeyboard: boolean = false,
  ): void {
    // Early return if no result or result type doesn't support preview
    if (!result || !this.isPreviewableType(result.type)) {
      this.previewResult = null;
      this.cameraSearchTerm = '';
      return;
    }

    // Set smart default for location preview (only when location changes)
    if (
      result.type === 'location' &&
      (!this.previewResult ||
        this.previewResult.type !== 'location' ||
        this.previewResult._id !== result._id)
    ) {
      // Default to edges if available, otherwise cameras, otherwise speakers, otherwise sub-locations
      const hasEdges = this.getLocationEdges(result).length > 0;
      const hasCameras = result.raw?.cameras?.length > 0;
      const hasSpeakers = this.getLocationSpeakers(result).length > 0;
      const hasSubLocations = this.getLocationSubLocations(result).length > 0;

      if (hasEdges) {
        this.activeLocationView = 'edges';
      } else if (hasCameras) {
        this.activeLocationView = 'cameras';
      } else if (hasSpeakers) {
        this.activeLocationView = 'speakers';
      } else if (hasSubLocations) {
        this.activeLocationView = 'sublocations';
      } else {
        // Fallback to edges even if empty (this ensures we always have an active tab)
        this.activeLocationView = 'edges';
      }
    }

    // Set smart default for sub-location preview (only when sub-location changes)
    if (
      result.type === 'sublocation' &&
      (!this.previewResult ||
        this.previewResult.type !== 'sublocation' ||
        this.previewResult._id !== result._id)
    ) {
      // Default to edges if available, otherwise cameras, otherwise speakers, otherwise sub-locations
      const hasEdges = this.getSubLocationEdges(result).length > 0;
      const hasCameras = result.raw?.cameras?.length > 0;
      const hasSpeakers = this.getSubLocationSpeakers(result).length > 0;
      const hasSubLocations =
        this.getSubLocationSubLocations(result).length > 0;

      if (hasEdges) {
        this.activeSubLocationView = 'edges';
      } else if (hasCameras) {
        this.activeSubLocationView = 'cameras';
      } else if (hasSpeakers) {
        this.activeSubLocationView = 'speakers';
      } else if (hasSubLocations) {
        this.activeSubLocationView = 'sublocations';
      } else {
        // Fallback to edges even if empty (this ensures we always have an active tab)
        this.activeSubLocationView = 'edges';
      }
    }

    // Set smart default for wall preview (only when wall changes)
    if (
      result.type === 'wall' &&
      (!this.previewResult ||
        this.previewResult.type !== 'wall' ||
        this.previewResult._id !== result._id)
    ) {
      // Default to edges if available, otherwise cameras, otherwise events
      const hasEdges = result.raw?.edges?.length > 0;
      const hasCameras = result.raw?.cameras?.length > 0;
      const hasEvents = result.raw?.events?.length > 0;

      if (hasEdges) {
        this.activeWallView = 'edges';
      } else if (hasCameras) {
        this.activeWallView = 'cameras';
      } else if (hasEvents) {
        this.activeWallView = 'events';
      } else {
        this.activeWallView = 'cameras'; // Fallback
      }
    }

    // Set smart default for event preview (only when event changes)
    if (
      result.type === 'event' &&
      (!this.previewResult ||
        this.previewResult.type !== 'event' ||
        this.previewResult._id !== result._id)
    ) {
      // Default to edges if available, otherwise cameras
      const hasEdges = result.raw?.edges?.length > 0;
      const hasCameras = result.raw?.cameras?.length > 0;

      if (hasEdges) {
        this.activeEventView = 'edges';
      } else if (hasCameras) {
        this.activeEventView = 'cameras';
      } else {
        this.activeEventView = 'edges'; // Fallback
      }
    }

    // Set smart default for speaker preview (only when speaker changes)
    if (
      result.type === 'speaker' &&
      (!this.previewResult ||
        this.previewResult.type !== 'speaker' ||
        this.previewResult._id !== result._id)
    ) {
      // Default to edges if available, otherwise cameras
      const hasEdges = this.getSpeakerEdges(result)?.length > 0;
      const hasCameras = result.raw?.cameras?.length > 0;

      if (hasEdges) {
        this.activeSpeakerView = 'edges';
      } else if (hasCameras) {
        this.activeSpeakerView = 'cameras';
      } else {
        this.activeSpeakerView = 'edges'; // Fallback
      }
    }

    // Set smart default for person preview (only when person changes)
    if (
      result.type === 'person' &&
      (!this.previewResult ||
        this.previewResult.type !== 'person' ||
        this.previewResult._id !== result._id)
    ) {
      // Default to edges if available, otherwise cameras, otherwise locations
      const hasEdges = result.raw?.edges?.length > 0;
      const hasCameras = result.raw?.cameras?.length > 0;
      const hasLocations = result.raw?.locations?.length > 0;

      if (hasEdges) {
        this.activePeopleView = 'edges';
      } else if (hasCameras) {
        this.activePeopleView = 'cameras';
      } else if (hasLocations) {
        this.activePeopleView = 'locations';
      } else {
        this.activePeopleView = 'edges'; // fallback
      }
    }

    // Set smart default for building preview (only when building changes)
    if (
      result.type === 'building' &&
      (!this.previewResult ||
        this.previewResult.type !== 'building' ||
        this.previewResult._id !== result._id)
    ) {
      // Default to floors if available, otherwise edges, otherwise cameras, otherwise locations
      const hasFloors = result.raw?.floors?.length > 0;
      const hasEdges = result.raw?.edges?.length > 0;
      const hasCameras = result.raw?.cameras?.length > 0;
      const hasLocations = result.raw?.locations?.length > 0;

      if (hasFloors) {
        this.activeBuildingView = 'floors';
      } else if (hasEdges) {
        this.activeBuildingView = 'edges';
      } else if (hasCameras) {
        this.activeBuildingView = 'cameras';
      } else if (hasLocations) {
        this.activeBuildingView = 'locations';
      } else {
        this.activeBuildingView = 'floors'; // fallback
      }
    }

    // Set smart default for floor preview (only when floor changes)
    if (
      result.type === 'floor' &&
      (!this.previewResult ||
        this.previewResult.type !== 'floor' ||
        this.previewResult._id !== result._id)
    ) {
      // Default to edges if available, otherwise cameras, otherwise locations
      const hasEdges = result.raw?.edges?.length > 0;
      const hasCameras = result.raw?.cameras?.length > 0;
      const hasLocations = result.raw?.locations?.length > 0;

      if (hasEdges) {
        this.activeFloorView = 'edges';
      } else if (hasCameras) {
        this.activeFloorView = 'cameras';
      } else if (hasLocations) {
        this.activeFloorView = 'locations';
      } else {
        this.activeFloorView = 'edges'; // fallback
      }
    }

    // Set the preview result
    this.previewResult = result;

    // Ensure we always have an active tab for location preview
    if (result.type === 'location' && !this.activeLocationView) {
      const hasEdges = this.getLocationEdges(result).length > 0;
      const hasCameras = result.raw?.cameras?.length > 0;
      const hasSpeakers = this.getLocationSpeakers(result).length > 0;
      const hasSubLocations = this.getLocationSubLocations(result).length > 0;

      if (hasEdges) {
        this.activeLocationView = 'edges';
      } else if (hasCameras) {
        this.activeLocationView = 'cameras';
      } else if (hasSpeakers) {
        this.activeLocationView = 'speakers';
      } else if (hasSubLocations) {
        this.activeLocationView = 'sublocations';
      } else {
        this.activeLocationView = 'edges';
      }
    }

    // Ensure we always have an active tab for sub-location preview
    if (result.type === 'sublocation' && !this.activeSubLocationView) {
      const hasEdges = this.getSubLocationEdges(result).length > 0;
      const hasCameras = result.raw?.cameras?.length > 0;
      const hasSpeakers = this.getSubLocationSpeakers(result).length > 0;
      const hasSubLocations =
        this.getSubLocationSubLocations(result).length > 0;

      if (hasEdges) {
        this.activeSubLocationView = 'edges';
      } else if (hasCameras) {
        this.activeSubLocationView = 'cameras';
      } else if (hasSpeakers) {
        this.activeSubLocationView = 'speakers';
      } else if (hasSubLocations) {
        this.activeSubLocationView = 'sublocations';
      } else {
        this.activeSubLocationView = 'edges';
      }
    }

    // Ensure we always have an active tab for wall preview
    if (result.type === 'wall' && !this.activeWallView) {
      const hasEdges = result.raw?.edges?.length > 0;
      const hasCameras = result.raw?.cameras?.length > 0;
      const hasEvents = result.raw?.events?.length > 0;

      if (hasEdges) {
        this.activeWallView = 'edges';
      } else if (hasCameras) {
        this.activeWallView = 'cameras';
      } else if (hasEvents) {
        this.activeWallView = 'events';
      } else {
        this.activeWallView = 'cameras';
      }
    }

    // Ensure we always have an active tab for event preview
    if (result.type === 'event' && !this.activeEventView) {
      const hasEdges = result.raw?.edges?.length > 0;
      const hasCameras = result.raw?.cameras?.length > 0;

      if (hasEdges) {
        this.activeEventView = 'edges';
      } else if (hasCameras) {
        this.activeEventView = 'cameras';
      } else {
        this.activeEventView = 'edges';
      }
    }

    // Ensure we always have an active tab for speaker preview
    if (result.type === 'speaker' && !this.activeSpeakerView) {
      const hasEdges = this.getSpeakerEdges(result)?.length > 0;
      const hasCameras = result.raw?.cameras?.length > 0;

      if (hasEdges) {
        this.activeSpeakerView = 'edges';
      } else if (hasCameras) {
        this.activeSpeakerView = 'cameras';
      } else {
        this.activeSpeakerView = 'edges';
      }
    }

    // Maintain keyboard selection when updating preview from keyboard
    if (isFromKeyboard) {
      this.isKeyboardSelected = true;
    }

    // Reset camera search when preview changes
    this.cameraSearchTerm = '';

    // Reset speaker search when preview changes
    this.speakerSearchTerm = '';

    // Reset sub-location search when preview changes
    this.subLocationSearchTerm = '';

    // Reset location search when preview changes
    this.locationSearchTerm = '';

    // Reset event search when preview changes
    this.eventSearchTerm = '';
  }

  /**
   * Check if a result type supports preview functionality
   * @param type The result type to check
   * @returns True if the type supports preview, false otherwise
   */
  private isPreviewableType(
    type: SearchGlobalModel.Common.CategoryId,
  ): boolean {
    const previewableTypes = [
      'camera',
      'event',
      'wall',
      'sublocation',
      'speaker',
      'location',
      'core',
      'alarm',
      'page',
      'article',
      'person',
      'building',
      'floor',
    ];

    // Using includes() for better performance with small arrays
    // includes() is more performant than some() for direct value comparison
    return previewableTypes.includes(type);
  }

  /**
   * Get human-readable match reason from Fuse.js matches
   * @param result The search result with matches array
   * @returns Human-readable string explaining what matched, or null if no matches
   */
  protected getMatchReason(result: any): string | null {
    if (this.globalSearchSettings?.includeAllMatch) {
      return SearchGlobalModalHelper.getMatchReason(result, this.translate);
    }
    return null;
  }

  /**
   * Get all match reasons for tooltip display
   * @param result The search result with matches array
   * @returns Array of formatted match reasons
   */
  protected getAllMatchReasons(result: any): string[] {
    return SearchGlobalModalHelper.getAllMatchReasons(result, this.translate);
  }

  /**
   * Get all match reasons for tooltip display with HTML highlighting
   * @param result The search result with matches array
   * @returns Array of formatted match reasons with HTML highlighting
   */
  protected getAllMatchReasonsWithHighlighting(result: any): string[] {
    const groups = SearchGlobalModalHelper.groupMatchReasonsWithHighlighting(
      result,
      this.translate,
      this.searchTerm,
      this.globalSearchSettings?.markResult ??
        this.defaultGlobalSearchSettings.markResult,
    );
    return Object.entries(groups).flatMap(([label, values]) =>
      values.map((v) => `${label}: ${v}`),
    );
  }

  // Custom tooltip properties
  isMatchReasonsTooltipVisible = false;
  tooltipPosition = { x: 0, y: 0 };
  groupedMatchReasons: { [key: string]: string[] } = {};
  groupedMatchReasonsWithHighlighting: { [key: string]: string[] } = {};
  private tooltipTimeout: any = null;

  /**
   * Show the custom match reasons tooltip
   */
  protected showMatchReasonsTooltip(result: any, event: MouseEvent): void {
    // Clear any existing timeout
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = null;
    }

    const matchReasons = this.getAllMatchReasons(result);
    if (matchReasons.length <= 1) return;

    // Group match reasons by field type
    this.groupedMatchReasons = this.groupMatchReasons(result);

    // Group match reasons with highlighting by field type
    this.groupedMatchReasonsWithHighlighting =
      this.groupMatchReasonsWithHighlighting(result);

    // Position tooltip
    this.tooltipPosition = {
      x: event.clientX + 10,
      y: event.clientY - 10,
    };

    this.isMatchReasonsTooltipVisible = true;
  }

  /**
   * Hide the custom match reasons tooltip
   */
  protected hideMatchReasonsTooltip(): void {
    // Add a small delay before hiding to allow moving to tooltip
    this.tooltipTimeout = setTimeout(() => {
      this.isMatchReasonsTooltipVisible = false;
    }, 150); // 150ms delay
  }

  /**
   * Handle mouse enter on tooltip - cancel hide timeout
   */
  protected onTooltipMouseEnter(): void {
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = null;
    }
  }

  /**
   * Handle mouse leave on tooltip - start hide timeout
   */
  protected onTooltipMouseLeave(): void {
    this.tooltipTimeout = setTimeout(() => {
      this.isMatchReasonsTooltipVisible = false;
    }, 100); // Shorter delay when leaving tooltip
  }

  /**
   * Highlight matched text in search results
   * @param text The text to highlight
   * @param searchTerm The search term to highlight
   * @returns HTML string with highlighted text
   */
  protected highlightMatch(text: string, searchTerm: string): string {
    return SearchGlobalModalHelper.highlightMatch(text, searchTerm);
  }

  /**
   * Highlight text using Fuse.js indices for precise highlighting
   * @param text The text to highlight
   * @param result The search result
   * @param fieldKey The field key to check
   * @returns HTML string with highlighted text
   */
  protected highlightMatchByFuseResult(
    text: string,
    result: any,
    fieldKey: string,
  ): string {
    if (!this.globalSearchSettings?.markResult || !text || !result.matches)
      return text;

    // Find the match for this specific field
    const match = result.matches.find((m) => m.key === fieldKey);
    if (!match || !match.indices) return text;

    // Use Fuse.js indices to highlight exactly what matched
    let highlightedText = text;

    // Sort indices in reverse order to avoid offset issues
    const sortedIndices = [...match.indices].sort((a, b) => b[0] - a[0]);

    // Apply highlighting from end to beginning
    sortedIndices.forEach(([start, end]) => {
      const before = highlightedText.substring(0, start);
      const matched = highlightedText.substring(start, end + 1);
      const after = highlightedText.substring(end + 1);

      highlightedText =
        before +
        '<mark class="search-highlight">' +
        matched +
        '</mark>' +
        after;
    });

    return highlightedText;
  }

  /**
   * Highlight text using Fuse.js indices for precise highlighting
   * @param text The text to highlight
   * @param result The search result with matches
   * @param fieldKey The field key to highlight (e.g., 'name', 'raw.cameras.cameraName')
   * @returns HTML string with highlighted text
   */
  protected highlightMatchByIndices(
    text: string,
    result: any,
    fieldKey: string,
  ): string {
    if (!text || !result.matches) return text;
    const match = result.matches.find((m: any) => m.key === fieldKey);
    return SearchGlobalModalHelper.highlightByIndices(
      text,
      match?.indices,
      this.globalSearchSettings?.markResult ??
        this.defaultGlobalSearchSettings.markResult,
    );
  }

  /**
   * Highlight only the value part of a match reason (not the label)
   * @param matchReason The full match reason string
   * @param searchTerm The search term to highlight
   * @returns HTML string with highlighted value only
   */
  protected highlightMatchReasonValue(
    matchReason: string,
    searchTerm: string,
  ): string {
    return SearchGlobalModalHelper.highlightMatchReasonValue(
      matchReason,
      searchTerm,
    );
  }

  /**
   * Get match reason with precise highlighting using Fuse.js indices
   * @param result The search result
   * @returns HTML string with highlighted match reason
   */
  protected getMatchReasonWithHighlighting(result: any): string {
    if (this.globalSearchSettings?.includeAllMatch) {
      return SearchGlobalModalHelper.getMatchReasonWithHighlighting(
        result,
        this.translate,
        this.globalSearchSettings?.markResult ??
          this.defaultGlobalSearchSettings.markResult,
      );
    }
    return null;
  }

  /**
   * Group match reasons by field type for better organization
   */
  private groupMatchReasons(result: any): { [key: string]: string[] } {
    return SearchGlobalModalHelper.groupMatchReasons(result, this.translate);
  }

  /**
   * Group match reasons by field type with precise HTML highlighting
   */
  private groupMatchReasonsWithHighlighting(result: any): {
    [key: string]: string[];
  } {
    return SearchGlobalModalHelper.groupMatchReasonsWithHighlighting(
      result,
      this.translate,
      this.searchTerm,
      this.globalSearchSettings?.markResult ??
        this.defaultGlobalSearchSettings.markResult,
    );
  }

  updatePreview(): void {
    // Don't update preview if mouse is actively in floor preview (camera cone interaction)
    if (this.isMouseInFloorPreview) {
      return;
    }

    if (this.activeIndex >= 0 && this.activeIndex < this.flatResults.length) {
      const activeResult = this.flatResults[this.activeIndex];
      this.setPreview(activeResult, true); // true = from keyboard
    } else {
      this.setPreview(null);
    }
  }

  enable(event: any, toggleEvent: Event): void {
    const target = toggleEvent.target as HTMLInputElement;
    this.store$.dispatch(
      AlertEventsActions.enableEvent({ alert: event, enabled: target.checked }),
    );
  }

  executeCommand(): void {
    const commandText = this.searchTerm.trim().substring(1); // Remove the '/' operator
    if (commandText) {
      this.router.navigate(['search-v2', 'text-search'], {
        queryParams: {
          searchTerm: commandText,
          selectedAllCameras: true,
        },
      });
      this.dialogRef.close();
    }
  }

  getEncodedSearchTerm(): string {
    const commandText = this.searchTerm.trim().substring(1);
    return encodeURIComponent(commandText);
  }

  /**
   * Navigate to a camera from speaker preview
   * @param camera The camera object with cameraId, edgeId, locationId
   */
  navigateToCamera(camera: SearchGlobalModel.Refs.CameraRef): void {
    if (camera?.cameraId && camera?.edgeId && camera?.locationId) {
      const route = [
        'cameras',
        'camera-view',
        camera.locationId,
        camera.edgeId,
        camera.cameraId,
      ];
      this.router.navigate(route);
      this.dialogRef.close();
    }
  }

  /**
   * Navigate to speaker from location preview
   * @param speaker The speaker object with speakerId and speakerName
   */
  navigateToSpeaker(speaker: SearchGlobalModel.Refs.SpeakerRef): void {
    if (speaker?.speakerName) {
      this.router.navigate(
        [
          routerSegments.locationV3,
          routerSegments.devices,
          routerSegments.list,
        ],
        {
          queryParams: {
            types: DeviceLocationModel.DeviceType.SPEAKER,
            term: speaker.speakerName,
          },
        },
      );
      this.dialogRef.close();
    }
  }

  /**
   * Navigate to sub-location from location preview
   * @param subLocation The sub-location object with subLocationId and subLocationName
   */
  navigateToSubLocation(
    subLocation: SearchGlobalModel.Refs.SubLocationRef,
  ): void {
    if (subLocation?.subLocationName) {
      const route = [
        routerSegments.locationV3,
        routerSegments.devices,
        routerSegments.list,
      ];
      this.router.navigate(route, {
        queryParams: { term: subLocation.subLocationName },
      });
      this.dialogRef.close();
    }
  }

  /**
   * Navigate to location from camera hierarchy
   * @param locationId The location ID to navigate to
   */
  navigateToLocationById(locationId: string): void {
    if (locationId) {
      this.router.navigate(['/home', locationId]);
      this.dialogRef.close();
    }
  }

  /**
   * Navigate to location by name (search-based navigation)
   * @param locationName The location name to search for
   */
  navigateToLocationByName(locationName: string): void {
    if (locationName) {
      const route = [
        routerSegments.locationV3,
        routerSegments.devices,
        routerSegments.list,
      ];
      this.router.navigate(route, {
        queryParams: { term: locationName },
      });
      this.dialogRef.close();
    }
  }

  /**
   * Navigate to edge/core from camera hierarchy
   * @param edgeId The edge ID to navigate to
   */
  navigateToEdgeById(edgeId: string): void {
    if (edgeId) {
      this.router.navigate(['/edge', edgeId]);
      this.dialogRef.close();
    }
  }

  /**
   * Get camera locations as array for reusable location list template
   * @param camera The camera result object
   * @returns Array with single location object
   */
  getCameraLocations(
    camera: any,
  ): { locationId: string; locationName: string }[] {
    if (!camera?.locationId || !camera?.locationName) return [];
    return [
      {
        locationId: camera.locationId,
        locationName: camera.locationName,
      },
    ];
  }

  /**
   * Get camera edges as array for reusable edge list template
   * @param camera The camera result object
   * @returns Array with single edge object
   */
  getCameraEdges(
    camera: any,
  ): { edgeId: string; edgeName: string; cameraCount?: number }[] {
    if (!camera?.edgeId || !camera?.edgeName) return [];
    return [
      {
        edgeId: camera.edgeId,
        edgeName: camera.edgeName,
        cameraCount: 1, // This camera belongs to this edge
      },
    ];
  }

  /**
   * Get edge locations as array for reusable location list template
   * @param edge The edge result object
   * @returns Array with single location object
   */
  getEdgeLocations(edge: any): { locationId: string; locationName: string }[] {
    if (!edge?.locationId || !edge?.locationName) return [];
    return [
      {
        locationId: edge.locationId,
        locationName: edge.locationName,
      },
    ];
  }

  /**
   * Location list helper methods
   */
  trackLocationBy(index: number, location: any): string {
    return location.locationId || index;
  }

  getLocationListHeight(count: number): number {
    const itemHeight = 80;
    const itemMargin = 16; // Total margin per item (8px top + 8px bottom)
    return Math.min(count * (itemHeight + itemMargin), 400); // Max height of 400px
  }

  onLocationSearchInput(event: any): void {
    // Simple search implementation - could be enhanced with debouncing
    this.locationSearchTerm = event.target.value;
  }

  getFilteredLocations(locations: any[], searchTerm: string): any[] {
    if (!searchTerm?.trim()) return locations;
    const term = searchTerm.toLowerCase();
    return locations.filter((location) =>
      location.locationName?.toLowerCase().includes(term),
    );
  }

  getFilteredEdges(edges: any[], searchTerm: string): any[] {
    if (!searchTerm?.trim()) return edges;
    const term = searchTerm.toLowerCase();
    return edges.filter((edge) => edge.edgeName?.toLowerCase().includes(term));
  }

  /**
   * Event search helper methods
   */
  onEventSearchInput(event: any): void {
    // Simple search implementation - could be enhanced with debouncing
    this.eventSearchTerm = event.target.value;
  }

  getFilteredEvents(events: any[], searchTerm: string): any[] {
    if (!searchTerm?.trim()) return events;
    const term = searchTerm.toLowerCase();
    return events.filter(
      (event) =>
        event.eventName?.toLowerCase().includes(term) ||
        event.sentence?.toLowerCase().includes(term),
    );
  }

  /**
   * Navigate to edit speaker page
   * @param speaker The speaker object with speakerId and locationId
   */

  /**
   * Get simple explanation of why a search result was selected
   * @param result The search result object
   * @returns Simple explanation string
   */
  getSimpleMatchReason(result: any): string {
    if (!result || !result.matches || !Array.isArray(result.matches)) {
      return '';
    }

    const reasons: string[] = [];
    const seenKeys = new Set<string>(); // Track unique keys

    result.matches.forEach((match: any) => {
      if (match.key && !seenKeys.has(match.key)) {
        // Only process unique keys
        seenKeys.add(match.key);

        switch (match.key) {
          case 'name':
            // Add category to make it more specific
            const categoryName =
              SearchGlobalModalHelper.getCategoryLocalizedLabel(
                this.translate,
                result.type as SearchGlobalModel.Common.CategoryId,
              );
            reasons.push(`${categoryName} name`);
            break;
          case 'subInfo':
            reasons.push(
              this.translate.instant('SEARCH_GLOBAL_MODAL.REASON.DESCRIPTION'),
            );
            break;
          case 'raw.city':
            reasons.push(
              this.translate.instant('SEARCH_GLOBAL_MODAL.REASON.CITY'),
            );
            break;
          case 'raw.address':
            reasons.push(
              this.translate.instant('SEARCH_GLOBAL_MODAL.REASON.ADDRESS'),
            );
            break;
          case 'raw.cameras.cameraName':
            reasons.push(
              this.translate.instant('SEARCH_GLOBAL_MODAL.REASON.CAMERA_NAME'),
            );
            break;
          case 'raw.edges.edgeName':
            reasons.push(
              this.translate.instant('SEARCH_GLOBAL_MODAL.REASON.EDGE_NAME'),
            );
            break;
          case 'raw.locations.locationName':
            reasons.push('Location');
            break;
          case 'raw.subLocations.subLocationName':
            reasons.push('Sub-location');
            break;
          case 'raw.speakers.speakerName':
            reasons.push(
              this.translate.instant('SEARCH_GLOBAL_MODAL.REASON.SPEAKER_NAME'),
            );
            break;
          case 'raw.retentionDays':
            reasons.push(
              this.translate.instant(
                'SEARCH_GLOBAL_MODAL.REASON.RETENTION_DAYS',
              ),
            );
            break;
          case 'raw.ipAddress':
            reasons.push(
              this.translate.instant('SEARCH_GLOBAL_MODAL.REASON.IP_ADDRESS'),
            );
            break;
          case 'raw.description':
            reasons.push('Description');
            break;
          default:
            // For any other fields, use the exact key as is
            reasons.push(match.key);
        }
      }
    });

    if (reasons.length === 0) return '';

    return reasons.length === 1
      ? `${reasons[0]} matches`
      : `${reasons.slice(0, -1).join(', ')} and ${reasons[reasons.length - 1]} match`;
  }

  editSpeaker(speaker: any): void {
    if (speaker?.speakerId && speaker?.locationId) {
      const route = [
        routerSegments.locationV3,
        routerSegments.devices,
        routerSegments.editSpeaker,
        speaker.locationId,
        speaker.speakerId,
      ];
      this.router.navigate(route);
      this.dialogRef.close();
    }
  }

  /**
   * Navigate to edit location page
   * @param location The location object with locationId
   */
  editLocation(location: SearchGlobalModel.Items.LocationItem): void {
    if (location?.locationId) {
      const route = [
        routerSegments.locationV3,
        routerSegments.devices,
        routerSegments.editLocation,
        location.locationId,
      ];
      this.router.navigate(route);
      this.dialogRef.close();
    }
  }

  /**
   * Navigate to edit edge/core page
   * @param edge The edge object with edgeId and locationId
   */
  editEdge(edge: SearchGlobalModel.Items.CoreItem): void {
    if (edge?.edgeId && edge?.locationId) {
      const route = [
        routerSegments.edgeEdit,
        edge.locationId,
        edge.edgeId,
        routerSegments.details,
      ];
      this.router.navigate(route);
      this.dialogRef.close();
    }
  }

  /**
   * Navigate to edge page (same behavior as core search items)
   * @param edge The edge object with edgeId and locationId
   */
  navigateToEdge(edge: SearchGlobalModel.Refs.EdgeRef): void {
    if (edge?.edgeName) {
      const route = [
        routerSegments.locationV3,
        routerSegments.devices,
        routerSegments.list,
      ];
      this.router.navigate(route, {
        queryParams: {
          term: edge.edgeName,
          types: DeviceLocationModel.DeviceType.CORE,
        },
      });
      this.dialogRef.close();
    }
  }

  addCameraToEdge(edge: any): void {
    if (edge?.edgeId && edge?.locationId) {
      this.store$.dispatch(
        CamerasActions.setCameraCreateType({
          selectedCameraCreateType: CamerasCreateType.scan,
        }),
      );
      const route = [
        '/',
        routerSegments.camera,
        routerSegments.create,
        edge.locationId,
        edge.edgeId,
        routerSegments.camera,
      ];
      this.router.navigate(route);
      this.dialogRef.close();
    }
  }

  /**
   * Navigate to edit camera page
   * @param camera The camera object with cameraId, edgeId, and locationId
   */
  editCamera(camera: SearchGlobalModel.Items.CameraItem, tab = routerSegments.details): void {
    if (camera?.cameraId && camera?.edgeId && camera?.locationId) {
      const route = [
        routerSegments.cameraEdit,
        routerSegments.edit,
        camera.locationId,
        camera.edgeId,
        camera.cameraId,
        tab,
      ];
      this.router.navigate(route);
      this.dialogRef.close();
    }
  }

  /**
   * Navigate to edit wall page
   * @param wall The wall object with wallId
   */
  editWall(wall: SearchGlobalModel.Items.WallItem): void {
    if (wall?.wallId) {
      const route = [routerSegments.wallV2, routerSegments.edit, wall.wallId];
      this.router.navigate(route);
      this.dialogRef.close();
    }
  }

  /**
   * Navigate to edit event page
   * @param event The event object with eventId
   */
  editEvent(event: SearchGlobalModel.Items.EventItem): void {
    if (event?.eventId) {
      const route = [
        routerSegments.alertsV2,
        routerSegments.edit,
        event.eventId,
      ];
      this.router.navigate(route);
      this.dialogRef.close();
    }
  }

  /**
   * Navigate to edit alarm page
   * @param alarm The alarm object with alarmId
   */
  editAlarm(alarm: SearchGlobalModel.Items.AlarmItem): void {
    if (alarm?.alarmId) {
      const route = [
        routerSegments.alertsV2,
        routerSegments.alarms,
        routerSegments.edit,
        alarm.alarmId,
      ];
      this.router.navigate(route);
      this.dialogRef.close();
    }
  }

  navigateToEvent(event: SearchGlobalModel.Refs.EventRef): void {
    if (event?.eventName) {
      const route = [routerSegments.alertsV2, routerSegments.list];
      this.router.navigate(route, {
        queryParams: { query: event.eventName },
      });
      this.dialogRef.close();
    }
  }

  navigateToSchedule(scheduleId: string): void {
    if (scheduleId) {
      const route = [
        routerSegments.database,
        routerSegments.schedule,
        routerSegments.edit,
        scheduleId,
      ];
      this.router.navigate(route);
      this.dialogRef.close();
    }
  }

  /**
   * Navigate to location list with edge's location name as search parameter
   * @param edge The edge result object with locationName
   */
  navigateToLocationFromEdge(edge: SearchGlobalModel.Refs.EdgeRef): void {
    if (edge.locationName) {
      const route = [
        routerSegments.locationV3,
        routerSegments.devices,
        routerSegments.list,
      ];
      this.router.navigate(route, {
        queryParams: { term: edge.locationName },
      });
      this.dialogRef.close();
    }
  }

  /**
   * Open camera health modal
   * @param camera The camera object with cameraId, edgeId, and locationId
   */
  openCameraHealth(camera: any): void {
    if (camera?.cameraId && camera?.edgeId) {
      const data: CameraStatsModalData = {
        cameraId: camera.cameraId,
        edgeId: camera.edgeId,
        edgeName: camera.raw?.edgeName || camera.name,
      };
      this.dialog.open(SingleCameraStatsModalComponent, {
        width: '90vh',
        panelClass: 'modal-no-padding',
        data: data,
      });
      this.dialogRef.close();
    }
  }

  /**
   * Open location health modal
   * @param location The location object with locationId
   */
  openLocationHealth(location: any): void {
    if (location?.locationId) {
      // Get the location data from the store
      this.locations$.pipe(take(1)).subscribe((locations) => {
        const locationData = locations.find(
          (loc) => loc._id === location.locationId,
        );
        if (locationData) {
          this.dialog.open(LocationHealthModalComponent, {
            width: '90vw',
            height: '90vh',
            panelClass: 'modal-no-padding',
            data: {
              location: locationData,
            },
          });
          this.dialogRef.close();
        }
      });
    }
  }

  selectCategory(category: SearchGlobalModel.Common.CategoryId): void {
    // Update the searchTerm using full-token strategy
    const current = this.searchTerm.trim();
    const tokens = current.split(/\s+/).filter((t) => t.length > 0);
    const label = SearchGlobalModalHelper.getCategoryLocalizedLabel(
      this.translate,
      category,
    );
    if (
      tokens.length === 0 ||
      !tokens[0].startsWith(this.categoryFilterOperator)
    ) {
      this.searchTerm = `--${label} `;
    } else {
      const last = tokens[tokens.length - 1];
      if (last.startsWith(this.categoryFilterOperator)) {
        tokens[tokens.length - 1] = `--${label}`;
      } else {
        tokens.push(`--${label}`);
      }
      // Deduplicate category tokens
      const seen = new Set<string>();
      const normalized: string[] = [];
      for (const tok of tokens) {
        if (tok.startsWith(this.categoryFilterOperator) && tok.length > 2) {
          const cRaw = tok.substring(2);
          const id = this.getMatchingCategoryId(cRaw) || cRaw.toLowerCase();
          if (!seen.has(id)) {
            seen.add(id);
            const lbl =
              SearchGlobalModalHelper.getCategoryLocalizedLabel(
                this.translate,
                id as SearchGlobalModel.Common.CategoryId,
              ) || cRaw;
            normalized.push(`--${lbl}`);
          }
        } else if (!tok.startsWith(this.categoryFilterOperator)) {
          normalized.push(tok);
        }
      }
      this.searchTerm = `${normalized.join(' ')} `; // keep trailing space so user can type next filter/term
    }
    // Immediately reflect selected categories
    const newTokens = this.searchTerm
      .trim()
      .split(/\s+/)
      .filter((t) => t.length > 0);
    const cats: SearchGlobalModel.Common.CategoryId[] = [];
    for (const tok of newTokens) {
      if (tok.startsWith(this.categoryFilterOperator) && tok.length > 2) {
        const cRaw = tok.substring(2);
        const id = this.getMatchingCategoryId(
          cRaw,
        ) as SearchGlobalModel.Common.CategoryId | null;
        if (id && this.availableCategories.includes(id) && !cats.includes(id)) {
          cats.push(id);
        }
      }
    }
    this.selectedCategories = cats;

    // Only show autocomplete for remaining categories if user explicitly requests it
    // Don't automatically show remaining categories after each selection
    this.filteredCategories = [];
    this.showCategoryAutocomplete = false;
    this.activeCategoryIndex = -1;
    this.suggestedCategoryFull = null;
    this.reopenAutocompleteOnFocus = false;
    // No-op for ghost metrics (transparent prefix overlay approach)
    setTimeout(() => this.updateGhostMetrics(), 0);
  }

  private navigateCategoryAutocomplete(direction: number): void {
    if (this.filteredCategories.length === 0) return;
    this.activeCategoryIndex += direction;
    if (this.activeCategoryIndex >= this.filteredCategories.length) {
      this.activeCategoryIndex = 0;
    } else if (this.activeCategoryIndex < 0) {
      this.activeCategoryIndex = this.filteredCategories.length - 1;
    }
    // Update inline suggested hint to reflect the highlighted item
    this.suggestedCategoryFull =
      this.filteredCategories[this.activeCategoryIndex] || null;
    // No-op for ghost metrics (transparent prefix overlay approach)
  }

  private scrollActiveCategoryIntoView(): void {
    setTimeout(() => {
      const el = document.querySelector(
        '.category-autocomplete .autocomplete-item.active',
      ) as HTMLElement | null;
      if (el) {
        el.scrollIntoView({ block: 'nearest' });
      }
    }, 0);
  }

  getCategorySearchTerm(): string {
    if (!this.isCategoryFilterMode) return '';
    const tokens = this.searchTerm
      .trim()
      .split(/\s+/)
      .filter((t) => t.length > 0);
    if (
      tokens.length === 0 ||
      !tokens[0].startsWith(this.categoryFilterOperator)
    )
      return '';
    const nonCat = tokens.filter(
      (t) => !t.startsWith(this.categoryFilterOperator),
    );
    return nonCat.join(' ');
  }

  getCategoryInput(): string {
    if (!this.isCategoryFilterMode) return '';
    const tokens = this.searchTerm
      .trim()
      .split(/\s+/)
      .filter((t) => t.length > 0);
    if (tokens.length === 0) return '';
    const last = tokens[tokens.length - 1];
    if (last.startsWith(this.categoryFilterOperator)) return last.substring(2);
    return '';
  }

  getSuggestedSuffix(): string {
    const frag = this.getCategoryInput();
    if (!frag || !this.suggestedCategoryFull) return '';
    const full = this.suggestedCategoryFull.toLowerCase();
    const f = frag.toLowerCase();
    if (!full.startsWith(f)) return '';
    return this.suggestedCategoryFull.substring(frag.length);
  }

  getGhostPrefix(): string {
    // Full prefix up to end of current fragment (used when transparent overlay is active)
    return `${this.getGhostLeftPrefix()}${this.getGhostFragmentPrefix()}`;
  }

  getGhostLeftPrefix(): string {
    if (!this.isCategoryFilterMode) return '';
    const s = this.searchTerm;
    const idx = s.lastIndexOf(this.categoryFilterOperator);
    if (idx < 0) return '';
    return s.slice(0, idx);
  }

  getGhostFragmentPrefix(): string {
    if (!this.isCategoryFilterMode) return '';
    const s = this.searchTerm;
    const idx = s.lastIndexOf(this.categoryFilterOperator);
    if (idx < 0) return '';
    const after = s.slice(idx + this.categoryFilterOperator.length);
    const frag = after.split(/\s+/)[0] || '';
    return `${this.categoryFilterOperator}${frag}`;
  }

  getCurrentFragmentWithPrefix(): string {
    if (!this.isCategoryFilterMode) return '';
    const s = this.searchTerm;
    const idx = s.lastIndexOf(this.categoryFilterOperator);
    if (idx < 0) return '';

    // Get everything up to the start of current fragment
    const beforeFragment = s.slice(0, idx);
    // Get the current fragment
    const after = s.slice(idx + this.categoryFilterOperator.length);
    const frag = after.split(/\s+/)[0] || '';
    const currentFragment = `${this.categoryFilterOperator}${frag}`;

    return beforeFragment + currentFragment;
  }

  getTextUpToCurrentFragment(): string {
    if (!this.isCategoryFilterMode) return this.searchTerm;

    const s = this.searchTerm;
    const idx = s.lastIndexOf(this.categoryFilterOperator);
    if (idx < 0) return s;

    // Get everything up to the current fragment
    const beforeFragment = s.slice(0, idx);
    // Get the current fragment being typed
    const after = s.slice(idx + this.categoryFilterOperator.length);
    const frag = after.split(/\s+/)[0] || '';
    const currentFragment = `${this.categoryFilterOperator}${frag}`;

    return beforeFragment + currentFragment;
  }

  // NEW: Get text exactly as user typed it (this should match input exactly)
  getExactTypedText(): string {
    return this.searchTerm;
  }

  isCurrentFragmentIncomplete(): boolean {
    if (!this.isCategoryFilterMode) return false;

    const frag = this.getCategoryInput();
    if (!frag) return false;

    // Only show suggestion if the current fragment is not a complete category
    return !this.availableCategories.includes(
      frag.toLowerCase() as SearchGlobalModel.Common.CategoryId,
    );
  }

  getTextBeforeCurrentFragment(): string {
    if (!this.isCategoryFilterMode) return '';

    const s = this.searchTerm;
    const idx = s.lastIndexOf(this.categoryFilterOperator);
    if (idx < 0) return s;

    return s.slice(0, idx);
  }

  getCurrentFragmentText(): string {
    if (!this.isCategoryFilterMode) return '';

    const s = this.searchTerm;
    const idx = s.lastIndexOf(this.categoryFilterOperator);
    if (idx < 0) return '';

    const after = s.slice(idx + this.categoryFilterOperator.length);
    const frag = after.split(/\s+/)[0] || '';
    return `${this.categoryFilterOperator}${frag}`;
  }

  getSuggestionPosition(): number {
    if (!this.searchInputRef) return 18;

    try {
      const input = this.searchInputRef.nativeElement;

      // Try using the browser's native cursor position
      const cursorPosition = input.selectionStart || input.value.length;
      const textUpToCursor = input.value.substring(0, cursorPosition);

      // Create a temporary canvas to measure text width more accurately
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (context) {
        // Get computed styles from the input
        const styles = window.getComputedStyle(input);
        context.font = `${styles.fontSize} ${styles.fontFamily}`;

        // Measure the text width
        const textWidth = context.measureText(textUpToCursor).width;

        // Account for input padding and scroll
        const inputPadding = 18;
        const scrollLeft = input.scrollLeft || 0;

        return Math.max(inputPadding, textWidth - scrollLeft + inputPadding);
      }

      // Fallback to span measurement
      if (this.textMeasurerRef) {
        const measurer = this.textMeasurerRef.nativeElement;
        measurer.textContent = textUpToCursor;
        const textWidth = measurer.offsetWidth;
        const inputPadding = 18;
        const scrollLeft = input.scrollLeft || 0;
        return Math.max(inputPadding, textWidth - scrollLeft + inputPadding);
      }

      return 18;
    } catch (error) {
      return 18;
    }
  }

  private updateGhostMetrics(): void {
    // No calculations needed - CSS handles everything
    this.ghostReady = this.isCategoryFilterMode && !!this.getSuggestedSuffix();
  }

  getBadgeType(type: string): string {
    // Map logical types to existing badge color classes
    if (!type) return '';
    return type === 'core' ? 'edge' : type;
  }

  getAlarmEvents(result: any): Array<{
    eventId?: any;
    eventName: string;
    sentence: string;
    sentenceCameraInfo: any;
  }> | null {
    if (!result) return null;
    const pick = (arr: any[]): any[] => (Array.isArray(arr) ? arr : []);
    const direct = pick(result.alarmEvents);
    const rawAlarm = pick(result.raw?.alarmEvents);
    let list = direct.length ? direct : rawAlarm.length ? rawAlarm : [];
    if (!list.length) {
      const rawEvents = pick(result.raw?.events);
      if (rawEvents.length) {
        list = rawEvents.map((e: any) => ({
          eventId: e.eventId || e._id || e.id,
          eventName: e.eventName || e.name || 'Event',
          sentence: e.sentence || '',
          sentenceCameraInfo: e.sentenceCameraInfo || null,
        }));
      }
    }
    if (!list.length) return null;
    // Ensure normalized fields
    return list.map((e: any) => ({
      eventId: e.eventId,
      eventName: e.eventName || e.name || 'Event',
      sentence: e.sentence || '',
      sentenceCameraInfo: e.sentenceCameraInfo || null,
    }));
  }

  getFormattedCategoriesString(): string {
    return this.availableCategories
      .map((cat) => this.translate.instant(this.getTypeI18nKey(cat)))
      .join(', ');
  }

  private getMatchingCategoryId(
    fragment: string,
  ): SearchGlobalModel.Common.CategoryId | null {
    const raw = (fragment || '').trim();
    if (!raw) return null;
    const frag = raw.toLowerCase();
    for (const id of this.availableCategories) {
      const idLc = id.toLowerCase();
      const labelLc = SearchGlobalModalHelper.getCategoryLocalizedLabel(
        this.translate,
        id,
      ).toLowerCase();
      if (idLc === frag || labelLc === frag) {
        return id;
      }
    }
    return null;
  }

  /**
   * Get category keys in the same order as availableCategories for consistent visual and keyboard navigation
   * @returns Array of category keys in the correct order
   */
  getOrderedCategoryKeys(): SearchGlobalModel.Common.CategoryId[] {
    if (this.isCategoryFilterMode && this.selectedCategories.length > 0) {
      // In category filter mode, show only selected categories in their selection order
      return this.selectedCategories.filter(
        (cat) => this.groupedResults[cat]?.length > 0,
      );
    } else {
      // In normal mode, show all categories that have results in availableCategories order
      return this.availableCategories.filter(
        (cat) => this.groupedResults[cat]?.length > 0,
      );
    }
  }

  private sortCategoriesForFragment(
    fragment: string,
    exclude: string[],
  ): string[] {
    return SearchGlobalModalHelper.sortCategoriesForFragment(
      this.translate,
      fragment,
      exclude,
      this.availableCategories,
    );
  }

  /**
   * Track function for virtual scrolling to optimize performance
   * @param index - The index of the camera in the array
   * @param camera - The camera object
   * @returns A unique identifier for the camera
   */
  trackCameraBy(index: number, camera: any): string {
    return camera?.cameraId || camera?.id || index.toString();
  }

  /**
   * Track function for virtual scrolling events to optimize performance
   * @param index - The index of the event in the array
   * @param event - The event object
   * @returns A unique identifier for the event
   */
  trackEventBy(index: number, event: any): string {
    return event?.eventId || event?.id || event?._id || index.toString();
  }

  /**
   * Track function for virtual scrolling edges to optimize performance
   * @param index - The index of the edge in the array
   * @param edge - The edge object
   * @returns A unique identifier for the edge
   */
  trackEdgeBy(index: number, edge: any): string {
    return edge?.edgeId || edge?.id || edge?._id || index.toString();
  }

  /**
   * Track function for virtual scrolling speakers to optimize performance
   * @param index - The index of the speaker in the array
   * @param speaker - The speaker object
   * @returns A unique identifier for the speaker
   */
  trackSpeakerBy(index: number, speaker: any): string {
    return (
      speaker?.speakerId || speaker?.id || speaker?._id || index.toString()
    );
  }

  /**
   * Track function for virtual scrolling sub-locations to optimize performance
   * @param index - The index of the sub-location in the array
   * @param subLocation - The sub-location object
   * @returns A unique identifier for the sub-location
   */
  trackSubLocationBy(index: number, subLocation: any): string {
    return (
      subLocation?.subLocationId ||
      subLocation?.id ||
      subLocation?._id ||
      index.toString()
    );
  }

  /**
   * Track function for building navigation virtual scrolling
   */
  trackBuildingNavigationBy(index: number, building: any): string {
    return (
      building?.buildingId || building?.id || building?._id || index.toString()
    );
  }

  /**
   * Track function for floor navigation virtual scrolling
   */
  trackFloorNavigationBy(index: number, floor: any): string {
    return floor?.floorId || floor?.id || floor?._id || index.toString();
  }

  /**
   * Filter building navigation list based on search term
   */
  getFilteredBuildingNavigation(
    buildings: any[],
    searchTerm: string = '',
  ): any[] {
    if (!searchTerm.trim()) return buildings || [];
    const term = searchTerm.toLowerCase().trim();
    return buildings.filter(
      (building) =>
        building?.buildingName?.toLowerCase().includes(term) ||
        building?.buildingId?.toLowerCase().includes(term),
    );
  }

  /**
   * Filter floor navigation list based on search term
   */
  getFilteredFloorNavigation(floors: any[], searchTerm: string = ''): any[] {
    if (!searchTerm.trim()) return floors || [];
    const term = searchTerm.toLowerCase().trim();
    return floors.filter(
      (floor) =>
        floor?.floorName?.toLowerCase().includes(term) ||
        floor?.floorId?.toLowerCase().includes(term),
    );
  }

  /**
   * Get height for building navigation virtual scroll viewport
   */
  getBuildingNavigationListHeight(length: number): number {
    const maxHeight = 400;
    const itemHeight = 80;
    const calculatedHeight = length * itemHeight;
    return Math.min(calculatedHeight, maxHeight);
  }

  /**
   * Get height for floor navigation virtual scroll viewport
   */
  getFloorNavigationListHeight(length: number): number {
    const maxHeight = 400;
    const itemHeight = 80;
    const calculatedHeight = length * itemHeight;
    return Math.min(calculatedHeight, maxHeight);
  }

  /**
   * Handle building navigation search input
   */
  onBuildingNavigationSearchInput(event: Event): void {
    this.buildingNavigationSearchTerm = (
      event.target as HTMLInputElement
    ).value;
  }

  /**
   * Handle floor navigation search input
   */
  onFloorNavigationSearchInput(event: Event): void {
    this.floorNavigationSearchTerm = (event.target as HTMLInputElement).value;
  }

  /**
   * Navigate to floor by ID
   */
  navigateToFloorById(floorId: string): void {
    if (floorId) {
      this.router.navigate(['/floors', floorId]);
    }
  }

  /**
   * Get optimized event list for rendering
   * @param events - Array of events
   * @returns Optimized event array
   */
  getOptimizedEventList(events: any[]): any[] {
    if (!events || events.length === 0) return [];

    // For virtual scrolling, we can return the full array
    // CDK will handle the rendering optimization
    return events;
  }

  /**
   * Filter cameras based on search term
   * @param cameras - Array of cameras to filter
   * @param searchTerm - Search term to filter by
   * @returns Filtered array of cameras
   */
  getFilteredCameras(cameras: any[], searchTerm: string = ''): any[] {
    if (!searchTerm || !searchTerm.trim()) {
      return cameras;
    }

    const term = searchTerm.toLowerCase().trim();
    return cameras.filter(
      (camera) =>
        (camera.cameraName && camera.cameraName.toLowerCase().includes(term)) ||
        (camera.cameraId && camera.cameraId.toLowerCase().includes(term)),
      //||
      // (camera.locationName && camera.locationName.toLowerCase().includes(term)) ||
      // (camera.edgeId && camera.edgeId.toLowerCase().includes(term))
    );
  }

  /**
   * Filter speakers based on search term
   * @param speakers - Array of speakers to filter
   * @param searchTerm - Search term to filter by
   * @returns Filtered array of speakers
   */
  getFilteredSpeakers(speakers: any[], searchTerm: string = ''): any[] {
    if (!searchTerm || !searchTerm.trim()) {
      return speakers;
    }

    const term = searchTerm.toLowerCase().trim();
    return speakers.filter(
      (speaker) =>
        (speaker.speakerName &&
          speaker.speakerName.toLowerCase().includes(term)) ||
        (speaker.speakerId && speaker.speakerId.toLowerCase().includes(term)),
    );
  }

  /**
   * Filter sub-locations based on search term
   * @param subLocations - Array of sub-locations to filter
   * @param searchTerm - Search term to filter by
   * @returns Filtered array of sub-locations
   */
  getFilteredSubLocations(subLocations: any[], searchTerm: string = ''): any[] {
    if (!searchTerm || !searchTerm.trim()) {
      return subLocations;
    }

    const term = searchTerm.toLowerCase().trim();
    return subLocations.filter(
      (subLocation) =>
        (subLocation.subLocationName &&
          subLocation.subLocationName.toLowerCase().includes(term)) ||
        (subLocation.subLocationId &&
          subLocation.subLocationId.toLowerCase().includes(term)),
    );
  }

  /**
   * Handle camera search input changes
   */
  onCameraSearchInput(event: any): void {
    this.cameraSearchTerm = event.target.value;
  }

  /**
   * Handle edge search input changes
   */
  onEdgeSearchInput(event: any): void {
    this.edgeSearchTerm = event.target.value;
  }

  /**
   * Handle speaker search input changes
   */
  onSpeakerSearchInput(event: any): void {
    this.speakerSearchTerm = event.target.value;
  }

  /**
   * Handle sub-location search input changes
   */
  onSubLocationSearchInput(event: any): void {
    this.subLocationSearchTerm = event.target.value;
  }

  /**
   * Calculate optimal height for edge list virtual scroll viewport
   * @param edgeCount Number of edges to display
   * @returns Height in pixels
   */
  getEdgeListHeight(edgeCount: number): number {
    if (edgeCount <= 0) return 0;

    const itemHeight = 80; // Height of each edge item

    // If 5 or more items, use fixed height (like camera list)
    // If less than 5, use exact height
    if (edgeCount >= 5) {
      return 400; // Fixed height for 5+ items
    } else {
      return edgeCount * itemHeight; // Exact height for <5 items
    }
  }

  /**
   * Calculate optimal height for camera list virtual scroll viewport
   * @param cameraCount Number of cameras to display
   * @returns Height in pixels
   */
  getCameraListHeight(cameraCount: number): number {
    if (cameraCount <= 0) return 0;

    const itemHeight = 80; // Height of each camera item
    const itemMargin = 16; // Total margin per item (8px top + 8px bottom)

    // For virtual scroll to work correctly, account for item height + margins
    // This prevents the blank space issue when scrolling
    return cameraCount * (itemHeight + itemMargin);
  }

  /**
   * Calculate optimal height for event list virtual scroll viewport
   * @param eventCount Number of events to display
   * @returns Height in pixels
   */
  getEventListHeight(eventCount: number): number {
    if (eventCount <= 0) return 0;

    const itemHeight = 80; // Height of each event item (updated to match others)
    const itemMargin = 16; // Total margin per item (8px top + 8px bottom)

    // For virtual scroll to work correctly, account for item height + margins
    // This prevents the blank space issue when scrolling
    return eventCount * (itemHeight + itemMargin);
  }

  /**
   * Calculate optimal height for speaker list virtual scroll viewport
   * @param speakerCount Number of speakers to display
   * @returns Height in pixels
   */
  getSpeakerListHeight(speakerCount: number): number {
    if (speakerCount <= 0) return 0;

    const itemHeight = 80; // Height of each speaker item
    const itemMargin = 16; // Total margin per item (8px top + 8px bottom)

    // For virtual scroll to work correctly, account for item height + margins
    // This prevents the blank space issue when scrolling
    return speakerCount * (itemHeight + itemMargin);
  }

  /**
   * Calculate optimal height for sub-location list virtual scroll viewport
   * @param subLocationCount Number of sub-locations to display
   * @returns Height in pixels
   */
  getSubLocationListHeight(subLocationCount: number): number {
    if (subLocationCount <= 0) return 0;

    const itemHeight = 80; // Height of each sub-location item
    const itemMargin = 16; // Total margin per item (8px top + 8px bottom)

    // For virtual scroll to work correctly, account for item height + margins
    // This prevents the blank space issue when scrolling
    return subLocationCount * (itemHeight + itemMargin);
  }

  /**
   * Get edges for a location result
   * @param result The location result object
   * @returns Array of edges with camera counts
   */
  getLocationEdges(result: any): any[] {
    // Use the edges property if available, otherwise fall back to extracting from cameras
    if (result?.raw?.edges && Array.isArray(result.raw.edges)) {
      return result.raw.edges;
    }

    if (!result?.raw?.cameras) return [];

    // Group cameras by edge to create edge objects
    const edgeMap = new Map<string, any>();

    result.raw.cameras.forEach((camera: any) => {
      const edgeId = camera.edgeId;
      if (!edgeId) return;

      if (!edgeMap.has(edgeId)) {
        edgeMap.set(edgeId, {
          edgeId: edgeId,
          edgeName:
            camera.edgeName ||
            this.translate.instant('SEARCH_GLOBAL_MODAL.UNNAMED_EDGE'),
          cameraCount: 0,
        });
      }
      edgeMap.get(edgeId).cameraCount++;
    });

    return Array.from(edgeMap.values());
  }

  /**
   * Get edges for a sub-location result
   * @param result The sub-location result object
   * @returns Array of edges with camera counts
   */
  getSubLocationEdges(result: any): any[] {
    // Use the edges property if available (now includes camera counts)
    if (result?.raw?.edges && Array.isArray(result.raw.edges)) {
      return result.raw.edges;
    }

    if (!result?.raw?.cameras) return [];

    // Fallback: Group cameras by edge to create edge objects
    const edgeMap = new Map<string, any>();

    result.raw.cameras.forEach((camera: any) => {
      const edgeId = camera.edgeId;
      if (!edgeMap.has(edgeId)) {
        edgeMap.set(edgeId, {
          edgeId: edgeId,
          edgeName:
            camera.edgeName ||
            this.translate.instant('SEARCH_GLOBAL_MODAL.UNNAMED_EDGE'),
          cameraCount: 0,
        });
      }
      edgeMap.get(edgeId).cameraCount++;
    });

    return Array.from(edgeMap.values());
  }

  /**
   * Get edges for a speaker result
   * @param result The speaker result object
   * @returns Array of edges with camera counts
   */
  getSpeakerEdges(result: any): any[] {
    // Use the edges property if available, otherwise fall back to extracting from cameras
    if (result?.raw?.edges && Array.isArray(result.raw.edges)) {
      return result.raw.edges;
    }

    if (!result?.raw?.cameras) return [];

    // Group cameras by edge to create edge objects
    const edgeMap = new Map<string, any>();

    result.raw.cameras.forEach((camera: any) => {
      const edgeId = camera.edgeId;
      if (!edgeMap.has(edgeId)) {
        edgeMap.set(edgeId, {
          edgeId: edgeId,
          edgeName:
            camera.edgeName ||
            this.translate.instant('SEARCH_GLOBAL_MODAL.UNNAMED_EDGE'),
          cameraCount: 0,
        });
      }
      edgeMap.get(edgeId).cameraCount++;
    });

    return Array.from(edgeMap.values());
  }

  /**
   * Get speakers for a location result
   * @param result The location result object
   * @returns Array of speakers
   */
  getLocationSpeakers(result: any): any[] {
    if (result?.raw?.speakers && Array.isArray(result.raw.speakers)) {
      return result.raw.speakers;
    }
    return [];
  }

  /**
   * Get speakers for a sub-location result
   * @param result The sub-location result object
   * @returns Array of speakers
   */
  getSubLocationSpeakers(result: any): any[] {
    if (result?.raw?.speakers && Array.isArray(result.raw.speakers)) {
      return result.raw.speakers;
    }
    return [];
  }

  /**
   * Get sub-locations for a location result
   * @param result The location result object
   * @returns Array of sub-locations
   */
  getLocationSubLocations(result: any): any[] {
    if (result?.raw?.subLocations && Array.isArray(result.raw.subLocations)) {
      return result.raw.subLocations;
    }
    return [];
  }

  /**
   * Get sub-locations for a sub-location result
   * @param result The sub-location result object
   * @returns Array of sub-locations
   */
  getSubLocationSubLocations(result: any): any[] {
    if (result?.raw?.subLocations && Array.isArray(result.raw.subLocations)) {
      return result.raw.subLocations;
    }
    return [];
  }

  /**
   * Set the active view for location preview
   * @param view The view to activate ('edges' or 'cameras')
   */
  setActiveLocationView(view: SearchGlobalModel.Views.LocationView): void {
    this.activeLocationView = view;
    this.focusSearchInput();
  }

  /**
   * Set the active view for sub-location preview
   * @param view The view to activate ('edges', 'cameras', or 'speakers')
   */
  setActiveSubLocationView(view: SearchGlobalModel.Views.LocationView): void {
    this.activeSubLocationView = view;
    this.focusSearchInput();
  }

  /**
   * Set the active view for wall preview
   * @param view The view to activate ('cameras', 'events', etc.)
   */
  setActiveWallView(view: SearchGlobalModel.Views.WallView): void {
    this.activeWallView = view;
    this.focusSearchInput();
  }

  /**
   * Set the active view for event preview
   * @param view The view to activate ('edges', 'cameras', etc.)
   */
  setActiveEventView(view: SearchGlobalModel.Views.EventView): void {
    this.activeEventView = view;
    this.focusSearchInput();
  }

  /**
   * Set the active view for speaker preview
   * @param view The view to activate ('edges', 'cameras', etc.)
   */
  setActiveSpeakerView(view: SearchGlobalModel.Views.SpeakerView): void {
    this.activeSpeakerView = view;
    this.focusSearchInput();
  }

  /**
   * Get the count of unique edges for height calculation
   * @param edges Array of edges (may contain duplicates)
   * @returns Count of unique edges
   */
  getUniqueEdgeCount(edges: any[]): number {
    if (!edges || !Array.isArray(edges)) return 0;

    // Use Set to get unique edge IDs
    const uniqueEdgeIds = new Set(edges.map((edge) => edge.edgeId));
    return uniqueEdgeIds.size;
  }

  /**
   * Filter people based on search term
   * @param people - Array of people to filter
   * @param searchTerm - Search term to filter by
   * @returns Filtered array of people
   */
  getFilteredPeople(people: any[], searchTerm: string = ''): any[] {
    if (!searchTerm || !searchTerm.trim()) {
      return people;
    }

    const term = searchTerm.toLowerCase().trim();
    return people.filter(
      (person) =>
        (person.name && person.name.toLowerCase().includes(term)) ||
        (person.personId &&
          person.personId.toString().toLowerCase().includes(term)),
    );
  }

  /**
   * Navigate to person from preview or list
   * @param person The person object with personId and name
   */
  navigateToPerson(person: SearchGlobalModel.Refs.PersonRef): void {
    const route = [routerSegments.database, routerSegments.people];
    this.router.navigate(route, {
      queryParams: { search: person.name || person.personId },
    });
    this.dialogRef.close();
  }

  /**
   * Navigate to buildings management
   * @param building The building object
   */
  navigateToBuilding(building: SearchGlobalModel.Refs.BuildingRef): void {
    if (building?.buildingId) {
      this.router.navigate([routerSegments.location, routerSegments.floors], {
        queryParams: {
          buildingId: building.buildingId,
          term: building.name,
        },
      });
      this.dialogRef.close();
    }
  }

  /**
   * Navigate to floor view
   * @param buildingId
   * @param floorId
   */
  navigateToFloor(buildingId: string, floorId: string): void {
    if (buildingId && floorId) {
      this.router.navigate([routerSegments.location, routerSegments.floors], {
        queryParams: { buildingId, floorId },
      });
      this.dialogRef.close();
    }
  }

  /**
   * Get person image path using PeopleService
   * @param person The person object
   * @param gcp Whether to use GCP URL (default true)
   * @returns Image URL string
   */
  getPersonImagePath(person: any, gcp: boolean = true): string {
    return this.peopleService.getPersonImagePath(person, gcp);
  }

  getPersonImageFallback(person: any): string {
    return this.peopleService.getPersonImagePath(person, false);
  }

  resolveFloorImageUrl(floorImage: string): string {
    if (!floorImage) {
      return '';
    }
    const path = extractOrgAssetPath(floorImage);
    const baseUrl = `${GO_TUNNEL_BASE_MAP[ThumbsBackendTunnelType.GoProxy]}/${StorageBucket.OrganizationAssets}`;
    return `${baseUrl}/${path}`;
  }

  resolveFloorImageFallback(floorImage: string): string {
    if (!floorImage) {
      return '';
    }
    const path = extractOrgAssetPath(floorImage);
    return `${organizationAssetsAwsUrl}/${path}`;
  }

  /**
   * Set the active view for people preview
   * @param view The view to activate ('cameras', 'edges', etc.)
   */
  setActivePeopleView(view: SearchGlobalModel.Views.PeopleView): void {
    this.activePeopleView = view;
    this.focusSearchInput();
  }

  setActiveBuildingView(view: SearchGlobalModel.Views.BuildingView): void {
    this.activeBuildingView = view;
    this.focusSearchInput();
  }

  setActiveFloorView(view: SearchGlobalModel.Views.FloorView): void {
    this.activeFloorView = view;
    this.focusSearchInput();
  }

  /**
   * Get people list height for virtual scrolling
   * @param peopleCount Number of people in the list
   * @returns Height in pixels
   */
  getPeopleListHeight(peopleCount: number): number {
    const maxHeight = 400;
    const itemHeight = 80;
    const calculatedHeight = Math.min(peopleCount * itemHeight, maxHeight);
    return Math.max(calculatedHeight, itemHeight); // Minimum one item height
  }

  /**
   * Track by function for people virtual scrolling
   * @param index Index of the item
   * @param person Person object
   * @returns Unique identifier for the person
   */
  trackPersonBy(index: number, person: any): any {
    return person?.personId || person?._id || index;
  }

  /**
   * Handle people search input
   * @param event Input event
   */
  onPeopleSearchInput(event: any): void {
    this.peopleSearchTerm = event.target.value;
  }

  // Building search term and helper methods
  buildingSearchTerm = '';

  getFilteredBuildings(buildings: any[], searchTerm: string): any[] {
    if (!searchTerm?.trim()) return buildings || [];
    const term = searchTerm.toLowerCase().trim();
    return (buildings || []).filter(
      (building) =>
        (building.name && building.name.toLowerCase().includes(term)) ||
        (building.buildingId &&
          building.buildingId.toString().toLowerCase().includes(term)),
    );
  }

  getBuildingListHeight(buildingCount: number): number {
    // Similar calculation to people list height
    const maxHeight = 240;
    const itemHeight = 80;
    const calculatedHeight = Math.min(buildingCount * itemHeight, maxHeight);
    return Math.max(calculatedHeight, itemHeight); // At least one item height
  }

  trackBuildingBy(index: number, building: any): string {
    return building?.buildingId || index.toString();
  }

  onBuildingSearchInput(event: any): void {
    this.buildingSearchTerm = event.target.value;
  }

  // Floor search term and helper methods
  floorSearchTerm = '';

  getFilteredFloors(floors: any[], searchTerm: string): any[] {
    if (!searchTerm?.trim()) return floors || [];
    const term = searchTerm.toLowerCase().trim();
    const res = (floors || []).filter(
      (floor) =>
        (floor.floorName && floor.floorName.toLowerCase().includes(term)) ||
        (floor.name && floor.name.toLowerCase().includes(term)) ||
        (floor.buildingName &&
          floor.buildingName.toLowerCase().includes(term)) ||
        (floor.floorId &&
          floor.floorId.toString().toLowerCase().includes(term)),
    );
    return res;
  }

  getFloorListHeight(floorCount: number): number {
    // Similar calculation to people list height
    const maxHeight = 240;
    const itemHeight = 80;
    const calculatedHeight = Math.min(floorCount * itemHeight, maxHeight);
    return Math.max(calculatedHeight, itemHeight); // At least one item height
  }

  trackFloorBy(index: number, floor: any): string {
    return floor?.floorId || index.toString();
  }

  onFloorSearchInput(event: any): void {
    this.floorSearchTerm = event.target.value;
  }

  /**
   * Calculate camera visual size for floor preview based on radius
   * @param radius Camera radius (0-1)
   * @returns Size in pixels
   */
  getCameraSize(radius: number): number {
    // Base size for the preview container (approximating the original component logic)
    const baseSize = 250; // Height of our preview container
    const multiplier = 0.3; // CAMERA_RADIUS_MULTIPLIER from original component
    const effectiveRadius = radius || 0.3; // Default radius if missing

    // Ensure minimum size for visibility - at least 60px for cone visibility
    const calculatedSize = baseSize * multiplier * effectiveRadius;
    const minSize = 60; // Minimum size to ensure cone visibility
    const maxSize = baseSize * 0.5;

    return Math.max(minSize, Math.min(calculatedSize, maxSize));
  }

  /**
   * Edit person - open edit dialog
   * @param person The person object to edit
   */
  editPerson(person: any): void {
    const personData = {
      person: {
        personId: Number(person.personId),
        name: person.name,
        bestImage: person.bestImage,
      },
      personDetails: {
        appearance: person.appearance,
        timestamp: person.timestamp,
      },
      cameraName: person.raw?.cameras?.[0]?.cameraName || '',
      locationName: person.raw?.cameras?.[0]?.locationName || '',
    };

    const dialogRef = this.dialog.open(EditExistingPersonDialogComponent, {
      data: personData,
      panelClass: 'medium-dialog',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Person was updated, refresh the search results if needed
      }
    });
  }

  /**
   * Delete person - show confirmation dialog
   * @param person The person object to delete
   */
  deletePerson(person: any): void {
    this.store$.dispatch(
      SharedActions.showConfirmModal({
        options: {
          type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
          msg: this.translateService.instant('DELETE_PEOPLE_MODAL.MSG', {
            name: person.name,
          }),
          title: this.translateService.instant('DELETE_PEOPLE_MODAL.TITLE'),
          confirm: this.translateService.instant('CONFIRM_MODALS.COMMON_YES'),
          disableClose: true,
          params: {
            person: {
              personId: Number(person.personId),
              name: person.name,
            },
          },
        },
      }),
    );
  }

  /**
   * Edit building - placeholder for future implementation
   * @param building The building object to edit
   */
  editBuilding(building: SearchGlobalModel.Items.BuildingItem): void {
    // TODO: Implement building edit dialog
  }

  /**
   * Edit floor - placeholder for future implementation
   * @param floor The floor object to edit
   */
  editFloor(floor: SearchGlobalModel.Items.FloorItem): void {
    // TODO: Implement floor edit dialog
  }

  /**
   * Focus the search input for the currently active list (if it has >10 items)
   */
  private focusSearchInput(): void {
    // Use setTimeout to ensure the DOM has updated after view change
    setTimeout(() => {
      // Try to find and focus the visible search input
      const searchInput = document.querySelector(
        '.location-content input[type="text"]:not([style*="display: none"])',
      ) as HTMLInputElement;
      if (searchInput) {
        searchInput.focus();
      }
    }, 100);
  }

  public sync(): void {
    this.store$.dispatch(SearchableActions.SearchableSync());
  }

  public openGlobalSearchSettingsDialog(): void {
    this.selectUserSettings$.pipe(take(1)).subscribe((settings) => {
      this.dialog
        .open(GlobalSearchSettingsModalComponent, {
          width: '572px',
          data: settings
            ? {
                globalSearchSettings: settings.globalSearchSettings,
              }
            : undefined,
        })
        .afterClosed()
        .subscribe((res) => {
          if (res) {
            this.store$.dispatch(
              UserSettingsActions.updateUserGlobalSearchSettingsSuccess({
                settings: {
                  ...settings,
                  globalSearchSettings: res,
                } as UserSettingsModels.UserSettingsDocument,
              }),
            );
          }
        });
    });
  }

  public isSelected(category: SearchGlobalModel.Common.CategoryId): boolean {
    return this.selectedCategories.includes(category);
  }

  public toggleCategory(
    category: SearchGlobalModel.Common.CategoryId,
    event?: Event,
  ): void {
    event?.stopPropagation();

    const nextCategories = this.isSelected(category)
      ? this.selectedCategories.filter(
          (selectedCategory) => selectedCategory !== category,
        )
      : [...this.selectedCategories, category];

    this.selectedCategories = nextCategories;
    this.searchTerm = nextCategories
      .map(
        (selectedCategory) => `--${this.translateCategory(selectedCategory)}`,
      )
      .join(' ');

    if (!nextCategories.length) {
      this.resetCategoryFilterState();
      this.clearResults();
      return;
    }

    this.onSearch();

    setTimeout(() => {
      this.menuTrigger?.updatePosition();
    }, 100);
  }

  public translateCategory(
    categoryId: SearchGlobalModel.Common.CategoryId,
  ): string {
    return SearchGlobalModalHelper.getCategoryLocalizedLabel(
      this.translate,
      categoryId,
    );
  }
}
